import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useError } from '../contexts/ErrorContext';
import { useAuth } from '../contexts/AuthContext';
import { useOptionalOrganization } from '../contexts/OrganizationContext';
import { TimedProgress, ProgressStep } from './TimedProgress';
import {
  Box,
  Flex,
  Text,
  Card,
  Button,
  Checkbox,
  Heading,
  Table,
  Badge,
  Separator,
  Tabs,
  ScrollArea,
  TextField,
  Dialog,
  TextArea,
  IconButton,
  Select,
  Callout,
  Tooltip
} from '@radix-ui/themes';
import {
  CalendarIcon,
  PersonIcon,
  StarIcon,
  QuestionMarkIcon,
  CheckIcon,
  Pencil1Icon,
  TrashIcon,
  PlusIcon,
  FileTextIcon,
  ExclamationTriangleIcon,
  InfoCircledIcon,
  Cross2Icon
} from '@radix-ui/react-icons';
import { formatFrequency, formatCadenceBadge, getCadenceBadgeColor, resolveQuestionCadence, cronToCadence } from '../utils/frequencyUtils';
import {
  formatPatientCarePlanHeading,
  GENERAL_CARE_PLAN_TITLE,
  isGeneralCarePlanDisease,
  isMergedGlobalDisease,
} from '../utils/carePlanDisplay';
import {
  GENERAL_CARE_PLAN_SELECTION_LABEL,
  getAutoGenerateCarePlanDiseases,
  getClinicalDiseaseOptionsForPlan,
  getInitialSelectedDiseases,
  getRefreshInitialSelectedDiseases,
  resolveRegenerateSelection,
  resolveGenerationSelectedDiseases,
} from '../utils/carePlanDiseaseSelection';
import { toast } from 'react-hot-toast';
import { patientApi } from '../services/api';
import { CarePlanResponse, CarePlanGoal, CarePlanQuestion, CarePlanTask, ProblemApprovalItem, PatientFriendlyPlan } from '../types/patient';
import { ICD10Code, getSuggestedICD10Codes } from '../constants/icd10Codes';
import TimeInput12h from './common/TimeInput12h';
import { maskDateInput, maskedDateToIso, isFutureMaskedDate, defaultExpirationMasked, DEFAULT_CARE_PLAN_REVIEW_MONTHS } from '../utils/dateInputMask';
import { generateCarePlanPdf } from '../utils/generateCarePlanPdf';
import { fetchCarePlanReviewsForPdf } from '../utils/carePlanPdfPayload';
import { normalizeOnboardingScriptTemplate, OnboardingScript } from '../utils/onboardingScript';
import { buildPatientOnboardingScript } from '../utils/patientOnboardingScript';
import OnboardingScriptDialog from './OnboardingScriptDialog';
import {
  autoPushCarePlanToEhr,
  AUTO_PUSH_SUBMITTED_TITLE,
  AUTO_PUSH_SUBMITTED_DETAIL,
  AUTO_PUSH_FAILURE_TITLE,
  AUTO_PUSH_FAILURE_DETAIL,
} from '../utils/autoPushCarePlanToEhr';
import { isFirstTimeEnrollment as computeIsFirstTimeEnrollment } from '../utils/carePlanRegenerationCta';
import {
  describeCarryover,
  recalculatedTargetFor,
  sameTarget,
} from '../utils/carePlanCarryover';
import { describeBaselineForGoal, type BaselineReading } from '../utils/ehrChanges';
import PcmIntakeReviewPanel from './PcmIntakeReviewPanel';
import { buildVopGoalEditTarget, VOICE_OF_PATIENT_REQUIRED_MESSAGE } from '../utils/voiceOfPatient';
import {
  CCM_ELIGIBILITY_ACTION,
  CCM_ELIGIBILITY_BODY,
  CCM_ELIGIBILITY_NOTICE,
  CCM_ELIGIBILITY_TITLE,
  approvalRefusalNotice,
  getCcmGenerateBlock,
} from '../utils/ccmEligibility';

// Stages mirror the backend care-plan workflow (generate_and_persist_care_plans_workflow):
// read chart → read problems/meds → review vitals/goals → LLM generation (the slow part) →
// validate coverage/ICD-10 → persist. Weights bias the dwell toward the LLM call.
const CARE_PLAN_PROGRESS_STEPS: ProgressStep[] = [
  { label: 'Reading patient chart…', weight: 1 },
  { label: 'Reading problems & medications…', weight: 1 },
  { label: 'Reviewing vitals & person-centered goals…', weight: 1 },
  { label: 'Generating care plan goals & tasks…', weight: 6 },
  { label: 'Validating coverage & ICD-10 codes…', weight: 2 },
  { label: 'Finalizing draft…', weight: 1 },
];

// --- Types ---

interface EditableCarePlanGoal extends CarePlanGoal {
  goal_index?: string;
  patient_id?: string;
  disease?: string;
  uploaded_ts?: string;
  approved?: boolean;
  // PHA-510: human-readable attribution populated by the EHR cron when this
  // goal was added/updated as a result of an Athena chart change. Rendered
  // inline so the clinician sees *why* the LLM proposed it.
  source_change_reason?: string | null;
}

interface EditableCarePlanQuestion extends CarePlanQuestion {
  question_index?: string;
  patient_id?: string;
  disease?: string;
  uploaded_ts?: string;
  approved?: boolean;
}

interface EditableCarePlanTask extends CarePlanTask {
  task_index?: string;
  patient_id?: string;
  disease?: string;
  uploaded_ts?: string;
  approved?: boolean;
  // PHA-510: see EditableCarePlanGoal.source_change_reason.
  source_change_reason?: string | null;
}

interface MatchedCarePlanDisease {
  template_id: string;
  display_name: string;
  /** PHA-1199: counts toward a CCM-only clinic's 2-condition minimum. */
  qualifying?: boolean;
  /** PHA-1207: RPM-justifying condition (only sent for an RPM patient). */
  rpm_justifying?: boolean;
}

interface EditableCarePlanDefinition {
  disease: string;
  uploaded_ts: string;
  goals: EditableCarePlanGoal[];
  questions: EditableCarePlanQuestion[];
  tasks: EditableCarePlanTask[];
  icd10_codes: ICD10Code[];
  diseases: string[];
  // PHA-842: authoritative general-plan flag from the draft candidates endpoint, so the
  // draft card heading and Print-Draft PDF title read "General Care Plan" for a no-Dx plan
  // instead of falling back to the ICD-10 heuristic.
  is_general?: boolean;
}

type ItemType = 'goal' | 'question' | 'task';

interface EditDialogState {
  open: boolean;
  type: ItemType | null;
  item: any;
  isNew: boolean;
  planIndex: number;
}

const EDIT_DIALOG_CLOSED: EditDialogState = {
  open: false, type: null, item: null, isNew: false, planIndex: 0
};

const TAB_ORDER = ['goals', 'questions', 'tasks', 'icd10'] as const;
type TabName = typeof TAB_ORDER[number];

// --- Helpers ---

const normalizeIcd10Code = (c: { code: string; description?: string; clinical_disease?: string }): ICD10Code => ({
  code: c.code,
  description: c.description || '',
  clinical_disease: c.clinical_disease?.trim() || undefined,
});

const groupIcd10ByClinicalDisease = (codes: ICD10Code[]): Record<string, ICD10Code[]> => {
  const grouped: Record<string, ICD10Code[]> = {};
  for (const code of codes) {
    const key = code.clinical_disease?.trim() || 'Other';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(code);
  }
  return grouped;
};

const matchClinicalDiseaseForProblem = (
  problem: ProblemApprovalItem,
  planDiseases: string[]
): string | undefined => {
  const problemLower = problem.problem.toLowerCase();
  const descLower = (problem.icd10_description || '').toLowerCase();
  for (const disease of planDiseases) {
    const diseaseLower = disease.toLowerCase();
    if (
      problemLower.includes(diseaseLower) ||
      diseaseLower.includes(problemLower) ||
      descLower.includes(diseaseLower)
    ) {
      return disease;
    }
  }
  return undefined;
};

const getICD10CodesFromProblems = (disease: string, approvedProblems: ProblemApprovalItem[]): ICD10Code[] => {
  const diseaseLower = disease.toLowerCase();
  const codesFromProblems: ICD10Code[] = [];

  for (const problem of approvedProblems) {
    const problemLower = problem.problem.toLowerCase();
    if (problemLower.includes(diseaseLower) || diseaseLower.includes(problemLower)) {
      if (problem.icd10_code) {
        codesFromProblems.push({
          code: problem.icd10_code,
          description: problem.icd10_description || '',
        });
      }
    }
  }

  return codesFromProblems;
};

/** Merged/global plans: use all approved problem-list ICD-10 codes (not disease-name match). */
const getICD10CodesFromApprovedProblems = (
  approvedProblems: ProblemApprovalItem[],
  planDiseases: string[] = []
): ICD10Code[] => {
  const seen = new Set<string>();
  const codes: ICD10Code[] = [];
  for (const problem of approvedProblems) {
    if (!problem.icd10_code) continue;
    const key = problem.icd10_code.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    codes.push({
      code: problem.icd10_code,
      description: problem.icd10_description || '',
      clinical_disease: matchClinicalDiseaseForProblem(problem, planDiseases),
    });
  }
  return codes;
};

const deriveIcd10CodesForPlan = (
  plan: {
    disease: string;
    diseases?: string[];
    icd10_codes?: { code: string; description?: string; clinical_disease?: string }[];
  },
  approvedProblems: ProblemApprovalItem[]
): ICD10Code[] => {
  if (plan.icd10_codes && plan.icd10_codes.length > 0) {
    return plan.icd10_codes.map((c) => normalizeIcd10Code(c));
  }
  const fromProblems =
    isMergedGlobalDisease(plan.disease) || isGeneralCarePlanDisease(plan.disease)
      ? getICD10CodesFromApprovedProblems(approvedProblems, plan.diseases ?? [])
      : getICD10CodesFromProblems(plan.disease, approvedProblems);
  return filterCodesByProblemList(fromProblems, approvedProblems);
};

const isGeneralCarePlan = (disease: string) =>
  disease.trim().toLowerCase() === 'general care plan';

const filterCodesByProblemList = (codes: ICD10Code[], approvedProblems: ProblemApprovalItem[]): ICD10Code[] => {
  const problemCodes = new Set(
    approvedProblems
      .map(p => p.icd10_code)
      .filter(Boolean)
  );
  return codes.filter(c => problemCodes.has(c.code));
};

const countPrimaryQuestions = (questions: { conditional?: any }[]) =>
  questions.filter(q => !q.conditional).length;

const buildEditableCarePlans = (
  definitions: CarePlanResponse['care_plan_definitions'],
  patientId: string,
  approvedProblems: ProblemApprovalItem[]
): EditableCarePlanDefinition[] =>
  definitions.map((plan) => ({
    disease: plan.disease,
    uploaded_ts: plan.uploaded_ts,
    goals: plan.goals.map((goal, i) => ({
      ...goal,
      goal_index: (i + 1).toString(),
      patient_id: patientId,
      disease: plan.disease,
      uploaded_ts: plan.uploaded_ts,
      approved: true
    })),
    questions: plan.questions.map((question, i) => ({
      ...question,
      question_index: (i + 1).toString(),
      patient_id: patientId,
      disease: plan.disease,
      uploaded_ts: plan.uploaded_ts,
      approved: true
    })),
    tasks: plan.tasks.map((task, i) => ({
      ...task,
      task_index: (i + 1).toString(),
      patient_id: patientId,
      disease: plan.disease,
      uploaded_ts: plan.uploaded_ts,
      approved: true
    })),
    // PHA-718: When the backend has persisted ICD-10 codes for this draft
    // (non-empty array), trust them as-is — they were already filtered to
    // patient-relevant codes by the PHA-401 problem-list filter on generation.
    // Only fall back to deriving codes from the problem list when the backend
    // returned nothing (pre-fix drafts or plans with no codes yet).
    icd10_codes: deriveIcd10CodesForPlan(plan, approvedProblems),
    diseases: plan.diseases ?? [],
    is_general: plan.is_general, // PHA-842: carry the authoritative general-plan flag
  }));

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0];
};

// --- ICD-10 Sub-component ---

const ConditionICD10Section: React.FC<{
  icd10Codes: ICD10Code[];
  planIndex: number;
  clinicalDiseaseOptions: string[];
  onAddCode: (code: ICD10Code, planIndex: number) => void;
  onRemoveCode: (codeToRemove: string, planIndex: number) => void;
}> = ({ icd10Codes, planIndex, clinicalDiseaseOptions, onAddCode, onRemoveCode }) => {
  const [editingCode, setEditingCode] = useState<string | null>(null); // code string of the row being edited
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [addClinicalDisease, setAddClinicalDisease] = useState(
    () => clinicalDiseaseOptions[0] || ''
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAddClinicalDisease((current) => {
      if (clinicalDiseaseOptions.length === 0) return '';
      const normalized = current.trim().toLowerCase();
      if (clinicalDiseaseOptions.some((d) => d.toLowerCase() === normalized)) return current;
      return clinicalDiseaseOptions[0];
    });
  }, [clinicalDiseaseOptions]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setEditingCode(null);
        setIsAdding(false);
        setSearchTerm('');
      }
    };
    if (editingCode !== null || isAdding) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingCode, isAdding]);

  const grouped = groupIcd10ByClinicalDisease(icd10Codes);

  const getFilteredSuggestions = (): ICD10Code[] => {
    // Disease-suggested codes for the plan's clinical diseases. Anything not listed can
    // still be entered via the "Add custom" option when the search yields no matches.
    const allCodes: ICD10Code[] = [];
    for (const disease of clinicalDiseaseOptions) {
      for (const code of getSuggestedICD10Codes(disease)) {
        if (!allCodes.some(c => c.code === code.code)) allCodes.push(code);
      }
    }
    // Exclude already-selected codes (except the one being edited)
    const filtered = allCodes.filter(c =>
      !icd10Codes.some(sel => sel.code === c.code && sel.code !== editingCode)
    );
    if (!searchTerm) return filtered;
    const lower = searchTerm.toLowerCase();
    return filtered.filter(c =>
      c.code.toLowerCase().includes(lower) || c.description.toLowerCase().includes(lower)
    );
  };

  const handleSelect = (code: ICD10Code, clinicalDisease: string) => {
    if (editingCode !== null) {
      onRemoveCode(editingCode, planIndex);
      onAddCode({ ...code, clinical_disease: clinicalDisease }, planIndex);
      setEditingCode(null);
    } else {
      onAddCode({ ...code, clinical_disease: clinicalDisease }, planIndex);
      setIsAdding(false);
    }
    setSearchTerm('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEditingCode(null);
      setIsAdding(false);
      setSearchTerm('');
    }
  };

  const suggestions = getFilteredSuggestions();
  const showCustomOption = searchTerm.trim() && suggestions.length === 0;

  const renderSearchDropdown = (clinicalDisease: string) => (
    <div ref={dropdownRef} style={{ position: 'relative', zIndex: 50 }}>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search by code or description..."
        style={{
          width: '100%',
          padding: '6px 10px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          fontSize: '13px',
          outline: 'none',
        }}
        autoFocus
      />
      <div style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        maxHeight: '200px',
        overflowY: 'auto',
        marginTop: '2px',
      }}>
        {suggestions.map((code) => (
          <div
            key={code.code}
            onClick={() => handleSelect(code, clinicalDisease)}
            style={{
              padding: '6px 8px',
              cursor: 'pointer',
              fontSize: '12px',
              borderBottom: '1px solid #f1f5f9',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
          >
            <Flex gap="2" align="center">
              <Badge color="blue" size="1" variant="soft">{code.code}</Badge>
              <Text size="1">{code.description}</Text>
            </Flex>
          </div>
        ))}
        {showCustomOption && (
          <div
            onClick={() => handleSelect({ code: searchTerm.trim().toUpperCase(), description: '' }, clinicalDisease)}
            style={{
              padding: '6px 8px',
              cursor: 'pointer',
              fontSize: '12px',
              backgroundColor: '#f0fdf4',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#dcfce7')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#f0fdf4')}
          >
            <Flex gap="2" align="center">
              <PlusIcon width="14" height="14" />
              <Text size="1">Add custom: <strong>{searchTerm.trim().toUpperCase()}</strong></Text>
            </Flex>
          </div>
        )}
        {suggestions.length === 0 && !showCustomOption && (
          <div style={{ padding: '8px', textAlign: 'center' }}>
            <Text size="1" style={{ color: '#94a3b8' }}>No matching codes</Text>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Box>
      <Flex justify="between" align="center" className="mb-4">
        <Text size="3" weight="medium" className="text-slate-700">
          ICD-10 Diagnosis Codes (for Billing)
        </Text>
        {!isAdding && editingCode === null && (
          <Button
            size="2"
            onClick={() => { setIsAdding(true); setSearchTerm(''); setEditingCode(null); }}
          >
            <PlusIcon width="14" height="14" />
            Add Code
          </Button>
        )}
      </Flex>

      <Card className="p-4">
        {isAdding && (
          <Box className="mb-4">
            {clinicalDiseaseOptions.length > 1 && (
              <Flex align="center" gap="2" className="mb-2">
                <Text size="1" className="text-slate-500">Condition:</Text>
                <Select.Root value={addClinicalDisease} onValueChange={setAddClinicalDisease}>
                  <Select.Trigger />
                  <Select.Content>
                    {clinicalDiseaseOptions.map((d) => (
                      <Select.Item key={d} value={d}>{d}</Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </Flex>
            )}
            {renderSearchDropdown(addClinicalDisease || clinicalDiseaseOptions[0] || '')}
          </Box>
        )}

        <Text size="2" weight="medium" className="mb-3 block">
          Selected Codes ({icd10Codes.length}):
        </Text>
        {icd10Codes.length === 0 && !isAdding ? (
          <Text size="2" className="text-slate-500">
            No ICD-10 codes selected. Click "Add Code" to add diagnosis codes.
          </Text>
        ) : (
          <Table.Root>
            <Table.Header>
              <Table.Row className="bg-slate-50">
                <Table.ColumnHeaderCell style={{ width: '120px' }}>Code</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: '80px' }}>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.entries(grouped).map(([clinicalDisease, codes]) => (
                <React.Fragment key={clinicalDisease}>
                  <Table.Row>
                    <Table.Cell colSpan={3} style={{ backgroundColor: '#f8fafc', paddingTop: '10px', paddingBottom: '6px' }}>
                      <Text size="2" weight="medium" className="text-slate-700">
                        {clinicalDisease}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                  {codes.map((code, index) => (
                    <Table.Row key={`${clinicalDisease}-${code.code}-${index}`} className="hover:bg-slate-50">
                      {editingCode === code.code ? (
                        <Table.Cell colSpan={3}>
                          {renderSearchDropdown(clinicalDisease)}
                        </Table.Cell>
                      ) : (
                        <>
                          <Table.Cell>
                            <Badge color="blue" variant="soft">{code.code}</Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="2">
                              {code.description || '-'}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Flex gap="1">
                              <IconButton size="1" variant="ghost" onClick={() => { setEditingCode(code.code); setSearchTerm(code.code); setIsAdding(false); }}>
                                <Pencil1Icon width="16" height="16" />
                              </IconButton>
                              <IconButton size="1" variant="ghost" color="red" onClick={() => onRemoveCode(code.code, planIndex)}>
                                <TrashIcon width="16" height="16" />
                              </IconButton>
                            </Flex>
                          </Table.Cell>
                        </>
                      )}
                    </Table.Row>
                  ))}
                </React.Fragment>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Card>
    </Box>
  );
};

// --- Main Component ---

/** Minimal patient demographics needed to build the draft PDF. */
interface PatientForPdf {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  patient_id?: string;
  apcm_tier?: number | null;
  // PHA-1164: lists RPM among the patient's programs on the PDF.
  has_rpm_device?: boolean | null;
  // PHA-1195: prints the draft in the patient's language.
  language?: string | null;
  problems?: any[];
  // PHA-865: full medication list for the "Full Medication List" PDF section (PCMH).
  medications?: any[];
  logo_path?: string;
  organization_name?: string;
  // PHA-832: person-centered (Voice of the Patient) data so the draft PDF renders
  // the "What Matters to You" section identically to the downloaded plan.
  voice_of_patient_enabled?: boolean | null;
  // PHA-866: hide target dates on goals & tasks in the PDF.
  no_target_dates_enabled?: boolean | null;
  person_centered_goals?: any[] | null;
  pcm_barriers?: any[] | null;
  gas_definitions?: any[] | null;
}

interface CarePlanApprovalProps {
  carePlanData: CarePlanResponse | null;
  patientId: string;
  onApprove: (surveys: any[], consentGiven: boolean) => void;
  onDeclineConsent: () => void;
  initialConsentStatus?: 'yes' | 'no' | null;
  loading?: boolean;
  onGenerateCarePlan: (selectedDiseases: string[]) => Promise<CarePlanResponse>;
  carePlanLoading: boolean;
  carePlanGenerated: boolean;
  approvedProblems?: ProblemApprovalItem[];
  reviewChangedSinceGeneration?: boolean;
  onRegenerateCarePlan?: (
    selectedDiseases: string[],
    /** PHA-1157: `resetToGuidelines` regenerates from the standard guidelines only, instead of
     *  from the previous approved plan's edits. */
    options?: { resetToGuidelines?: boolean },
  ) => Promise<CarePlanResponse>;
  /** PHA-737: Patient demographics for building the draft PDF (Print Draft). */
  patientForPdf?: PatientForPdf;
  /** PHA-866: org hides target dates on goals & tasks (renders "Due: N/A", hides
   *  the Due Date column, and disables the date editors). */
  noTargetDatesEnabled?: boolean;
  /** PHA-1080: the clinic's care plan review cycle in months, resolved by the parent
   *  (nearest-ancestor, default 6). Seeds the review-date field below, whose value is
   *  what updateCarePlanExpiration writes over the backend's own fallback on approve --
   *  so this, not the backend default, is the number that reaches the database from
   *  the wizard. Resolved asynchronously by the parent, so it can arrive AFTER first
   *  render; the effect below re-seeds an untouched field when it does. */
  carePlanReviewMonths?: number;
  /** PHA-895: rendered inside the onboarding wizard. When true, approval defers survey
   *  generation and finalizes onboarding (records consent + generates surveys) so a patient is
   *  never surveyed before they are fully registered. Also enables draft autosave + the
   *  unsaved-changes guard. Existing-patient re-approval (PatientDetail) leaves this false. */
  isOnboarding?: boolean;
  /** PHA-895: bubble "there are unsaved edits vs. the session-entry baseline" up so the
   *  Dashboard can prompt Save/Discard before leaving the onboarding tab. */
  onUnsavedChangesChange?: (hasUnsaved: boolean) => void;
  /** PHA-895: register imperative Save / Discard handlers so the Dashboard exit modal can drive
   *  them. Called with null on unmount to deregister. */
  registerDraftControls?: (
    controls: { save: () => Promise<void>; discard: () => Promise<void> } | null,
  ) => void;
  /** PHA-967: onboarding-only "Save as Draft" next to Approve. Fired AFTER the draft has been
   *  persisted; the host leaves the wizard and opens the patient's chart on Care Plans so the
   *  clinician reviews what they just saved instead of hunting for an exit. */
  onSaveDraftAndExit?: () => void;
  /** PHA-1049: care-plan REFRESH only — "Cancel Care Plan Regeneration". Set by the host
   *  when the wizard was entered from the chart's Regenerate CTA rather than for a first-time
   *  onboarding. Discards the draft this regeneration produced and returns to the chart with
   *  the existing approved plan untouched; the host owns the navigation. Absent (and so not
   *  rendered) during onboarding, where there is no previous plan to fall back to and
   *  cancelling would leave the patient with nothing. */
  onCancelRegeneration?: () => Promise<void> | void;
  /** PHA-1047: care-plan REFRESH only — the conditions the patient's CURRENT approved plan
   *  focuses on. Those options come up pre-checked so the clinician continues from the
   *  existing plan instead of rebuilding the selection from scratch, and can edit freely.
   *  Undefined during a first-time onboarding, where PHA-707's "no pre-selection, make it a
   *  hard stop" rule applies instead. */
  refreshPreselectDiseases?: string[];
  /** PHA-945/PHA-1050: does the patient have an APPROVED plan to fall back on if they
   *  cancel? A draft-only patient does not, and telling them "the patient keeps their
   *  current approved care plan" would be a lie. */
  refreshHasApprovedPlan?: boolean;
  /** PHA-897/PHA-726: care-plan REFRESH only. The refresh flow collects NO consent — the
   *  patient is already enrolled, and re-consenting overwrote patients.consent_date, which
   *  is the billing start date (PHA-953). It collects an acknowledgment instead: the
   *  clinician confirms they went through the revised plan with the patient. Set by the
   *  host so this component can swap the consent radios for the checkbox. */
  isCarePlanRefresh?: boolean;
  /** PHA-1162: called after a Voice-of-the-Patient goal is saved from the approval step, so
   *  the host re-reads the patient and passes the fresh goals back in via `patientForPdf`
   *  (which is also what keeps the draft PDF's "Voice of the patient" section current). */
  onVoiceOfPatientSaved?: () => Promise<void> | void;
}

interface SurveyResult {
  name: string;
  frequency: string;
  url: string;
}

const CarePlanApproval: React.FC<CarePlanApprovalProps> = ({
  carePlanData,
  patientId,
  onApprove,
  onDeclineConsent,
  initialConsentStatus = null,
  loading = false,
  onGenerateCarePlan,
  carePlanLoading,
  carePlanGenerated,
  approvedProblems = [],
  reviewChangedSinceGeneration = false,
  onRegenerateCarePlan,
  patientForPdf,
  noTargetDatesEnabled = false,
  carePlanReviewMonths = DEFAULT_CARE_PLAN_REVIEW_MONTHS,
  isOnboarding = false,
  onUnsavedChangesChange,
  registerDraftControls,
  onSaveDraftAndExit,
  onCancelRegeneration,
  refreshPreselectDiseases,
  refreshHasApprovedPlan,
  isCarePlanRefresh,
  onVoiceOfPatientSaved,
}) => {
  const { showError } = useError();
  const { user } = useAuth();
  // PHA-1204: the active org, passed to the patient re-read the automatic EHR upload makes —
  // the same access context the chart loads the patient with (PHA-527).
  const orgId = useOptionalOrganization()?.orgId ?? null;

  /**
   * PHA-1119: is this approval the patient's ENROLMENT, or an update to a plan they are
   * already enrolled under?
   *
   * `isCarePlanRefresh` used to answer both of two different questions at once:
   *
   *   * *mechanics* — "is this a staged-review regeneration?", which decides whether the
   *     item review is staged rather than committed, whether generation reads the staged
   *     set, and whether Cancel is offered. That stays on `isCarePlanRefresh` /
   *     `refreshSource` and is unchanged.
   *   * *enrolment* — "has this patient already consented to the programme?", which decides
   *     whether this screen collects first-time consent or the PHA-726/897 "reviewed with
   *     patient" acknowledgment, and whether approval runs completeOnboarding.
   *
   * They were the same question while the regeneration flow was reachable only for patients
   * with an APPROVED plan (PHA-1073). PHA-1119 opens it to a patient who is still
   * mid-onboarding but already has a draft — their regeneration IS their onboarding, and
   * routing them through the acknowledgment would leave them enrolled with no consent on
   * file and no `completeOnboarding` call, which is exactly the hole PHA-1073 closed.
   *
   * `refreshHasApprovedPlan` is supplied by OnboardingWorkflow from the same `patient
   * .care_plan_definitions` (approved-only) list PatientDetail's `needsContinueOnboarding`
   * reads, on every refresh-source path. It is `undefined` on the resume path, where
   * `shouldResumeAsRefresh` has already established an approved plan exists — so only an
   * explicit `false` flips this, and anything unknown keeps today's behaviour.
   */
  const isFirstTimeEnrollment = computeIsFirstTimeEnrollment({
    isCarePlanRefresh: !!isCarePlanRefresh,
    refreshHasApprovedPlan,
  });

  // PHA-1162: for a PCMH clinic (voice_of_patient_enabled) Voice of the Patient is part of
  // care plan creation — one patient-level section between the conditions' clinical goals and
  // the General questions, and the plan cannot be approved without at least one saved goal.
  // The goals come from the same patient read that feeds the draft PDF; the backend enforces
  // the same rule on approve.
  const voiceOfPatientRequired = patientForPdf?.voice_of_patient_enabled === true;
  const vopGoals: any[] = Array.isArray(patientForPdf?.person_centered_goals)
    ? (patientForPdf!.person_centered_goals as any[])
    : [];
  const voiceOfPatientMissing = voiceOfPatientRequired && vopGoals.length === 0;
  const [vopWizardOpen, setVopWizardOpen] = useState(false);
  const [vopEditingGoal, setVopEditingGoal] = useState<any | null>(null);
  const vopSectionRef = useRef<HTMLDivElement | null>(null);

  // PHA-1142: the readings this draft's BP / BMI targets were calculated from, stamped by the
  // generator on the draft row. Re-read whenever a (re)generation lands. Best-effort: without
  // it the goals simply show no "calculated from" line.
  const [targetBaseline, setTargetBaseline] = useState<Record<string, BaselineReading> | null>(null);
  useEffect(() => {
    if (!patientId || !carePlanData) return;
    let cancelled = false;
    patientApi
      .getObservationHistory(patientId)
      .then((history) => {
        if (!cancelled) setTargetBaseline(history?.baselines?.draft?.vitals || null);
      })
      .catch((err) => console.warn('PHA-1142: could not read the target baseline:', err));
    return () => {
      cancelled = true;
    };
  }, [patientId, carePlanData]);

  // --- State ---

  const [activeTabs, setActiveTabs] = useState<Record<number, string>>({});
  const [editableCarePlans, setEditableCarePlans] = useState<EditableCarePlanDefinition[]>(() =>
    carePlanData ? buildEditableCarePlans(carePlanData.care_plan_definitions, patientId, approvedProblems) : []
  );
  const [editDialog, setEditDialog] = useState<EditDialogState>(EDIT_DIALOG_CLOSED);
  // PHA-626: personalized onboarding script modal
  const [onboardingScript, setOnboardingScript] = useState<OnboardingScript | null>(null);
  const [onboardingScriptNotice, setOnboardingScriptNotice] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  // PHA-1204: the approved plan is being uploaded to the EHR (clinics with automatic upload).
  const [uploadingToEhr, setUploadingToEhr] = useState(false);
  // PHA-967: explicit "Save as Draft" (distinct from the PHA-895 debounced autosave).
  const [savingDraft, setSavingDraft] = useState(false);
  // PHA-1049: cancelling a regeneration destroys work, so it asks first.
  // PHA-897: "Care plan reviewed with patient" — required before Approve in the refresh flow.
  const [reviewedWithPatient, setReviewedWithPatient] = useState(false);
  const [cancelRegenerationOpen, setCancelRegenerationOpen] = useState(false);
  // PHA-1157: confirm before "Reset to standard guidelines" throws the draft's edits away.
  const [resetToGuidelinesOpen, setResetToGuidelinesOpen] = useState(false);
  const [resettingToGuidelines, setResettingToGuidelines] = useState(false);
  const [cancellingRegeneration, setCancellingRegeneration] = useState(false);
  // PHA-406 V2: Track user-edited question frequencies (prompt -> cron/unscheduled)
  const [questionFrequencyOverrides, setQuestionFrequencyOverrides] = useState<Record<string, string>>({});
  // PHA-406 V2: Editable general questions (non-disease-specific).
  // PHA-734: Do NOT hardcode the hospitalization question here. For APCM-eligible patients
  // the hospitalization question is folded into the backend global plan at generate time
  // (PHA-340/PHA-618 gate), so it arrives in carePlanData.care_plan_definitions already.
  // Injecting it here produced a duplicate rendering in the review modal.
  const [generalQuestions, setGeneralQuestions] = useState<any[]>([]);
  const [consentStatus, setConsentStatus] = useState<'yes' | 'no' | null>(initialConsentStatus);
  // PHA-1080: seeded from the clinic's review cycle. carePlanReviewMonths starts at the
  // 6-month default and is replaced when the parent's lookup resolves, so this initial value
  // can be wrong for a 12-month clinic for one render -- the re-seed effect below corrects it,
  // but ONLY while the field is still the untouched default. Once the clinician types a date
  // or a saved draft restores one, the resolved cycle must not overwrite it.
  const [expirationDate, setExpirationDate] = useState<string>(() => defaultExpirationMasked(carePlanReviewMonths)); // holds MM/DD/YYYY
  const expirationDateTouchedRef = useRef(false);

  // PHA-1080: re-seed the review-date field once the clinic's cycle resolves. The parent's
  // lookup is async, so first render always uses the 6-month default. Without this, a
  // 12-month clinic would approve every plan on a 6-month review date — the exact bug this
  // ticket fixes, just relocated from a hardcoded constant into a race. Guarded on the
  // touched ref so it can never overwrite a date the clinician typed or a draft restored.
  useEffect(() => {
    if (expirationDateTouchedRef.current) return;
    setExpirationDate(defaultExpirationMasked(carePlanReviewMonths));
  }, [carePlanReviewMonths]);

  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [matchedDiseases, setMatchedDiseases] = useState<MatchedCarePlanDisease[]>([]);
  const [suggestGeneral, setSuggestGeneral] = useState(false);
  // PHA-1199: the clinic's qualifying-condition minimum (2 for a CCM-only clinic, else 0).
  const [minQualifyingConditions, setMinQualifyingConditions] = useState(0);
  // PHA-1207: this patient is on RPM, so one RPM-justifying condition satisfies the minimum.
  const [rpmOneDxAllowed, setRpmOneDxAllowed] = useState(false);
  const [selectedDiseases, setSelectedDiseases] = useState<Set<string>>(new Set());
  const autoGenerateKeyRef = useRef<string | null>(null);

  // --- PHA-895: draft autosave + unsaved-changes guard (onboarding only) ---
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Serialized snapshot of everything a draft persists — drives edit detection AND revert.
  const stateKey = useMemo(
    () => JSON.stringify({
      editableCarePlans,
      questionFrequencyOverrides,
      generalQuestions,
      consentStatus,
      expirationDate,
    }),
    [editableCarePlans, questionFrequencyOverrides, generalQuestions, consentStatus, expirationDate],
  );
  const stateKeyRef = useRef(stateKey);
  useEffect(() => { stateKeyRef.current = stateKey; }, [stateKey]);
  // Latest editable state, read by the stable Save/Discard handlers.
  const liveStateRef = useRef({
    editableCarePlans, questionFrequencyOverrides, generalQuestions, expirationDate, consentStatus,
  });
  useEffect(() => {
    liveStateRef.current = {
      editableCarePlans, questionFrequencyOverrides, generalQuestions, expirationDate, consentStatus,
    };
  }, [editableCarePlans, questionFrequencyOverrides, generalQuestions, expirationDate, consentStatus]);

  // Baseline = state at load (or after Discard/approve). Discard reverts to it; the unsaved-changes
  // prompt fires whenever the live state differs from it. lastSaved tracks what's on the server so
  // autosave doesn't re-POST an unchanged draft.
  const baselineKeyRef = useRef<string | null>(null);
  const lastSavedKeyRef = useRef<string | null>(null);
  const [draftMetaLoaded, setDraftMetaLoaded] = useState(!isOnboarding); // non-onboarding never loads draft meta

  /** The plain-English plan is not part of editableCarePlans, so read it from the source. */
  const patientFriendlyPlanFor = useCallback((plan: EditableCarePlanDefinition): PatientFriendlyPlan | null => {
    const sourceDef = carePlanData?.care_plan_definitions?.find(
      (d: any) => d.disease === plan.disease && d.uploaded_ts === plan.uploaded_ts,
    );
    return sourceDef?.patient_friendly_plan ?? null;
  }, [carePlanData]);

  // PHA-896: always read the latest parent callback without adding it to persistDraft's deps
  // (which would churn the debounced-autosave effect and could starve the 1.5s timer).
  const onUnsavedChangesChangeRef = useRef(onUnsavedChangesChange);
  onUnsavedChangesChangeRef.current = onUnsavedChangesChange;

  const persistDraft = useCallback(async (key: string) => {
    const s = liveStateRef.current;
    await patientApi.saveCarePlanDraft(
      patientId, s.editableCarePlans, s.questionFrequencyOverrides, s.generalQuestions,
      s.expirationDate, s.consentStatus,
    );
    lastSavedKeyRef.current = key;
    // PHA-896: a successful save makes the plan clean — clear the exit prompt so "Save Draft"
    // isn't followed by a spurious Save/Discard dialog. Skip if the user edited again while the
    // save was in flight (live state moved past the key we just persisted); the next autosave
    // handles that state.
    if (stateKeyRef.current === key) {
      setHasUnsavedChanges(false);
      onUnsavedChangesChangeRef.current?.(false);
    }
  }, [patientId]);

  // Restore the draft-scoped expiration date + consent selection when a saved draft is resumed.
  useEffect(() => {
    if (!isOnboarding || !patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const meta = await patientApi.getCarePlanDraftMeta(patientId);
        if (cancelled) return;
        // PHA-944: a REFRESH must not inherit a review date from a previous approval.
        // Observed on dev: patient 143138's draft meta still held 02/13/2027 from the plan
        // approved on Aug 13. Resuming a refresh on Aug 20 restored it into the field, and
        // updateCarePlanExpiration then wrote it back over the server's approved_ts + 6
        // months — so the "new" plan carried the OLD plan's review date, which is exactly
        // what this ticket exists to stop.
        //
        // Confirmed with Addi 2026-08-20: an approved plan's review date is approval + 6
        // months, every time. Only a date the clinician sets during THIS approval wins, and
        // that is the on-screen value, not a restored one. Onboarding keeps the restore —
        // there is no previous approval to inherit from, so it can only be the clinician's
        // own unfinished draft.
        //
        // PHA-1119: that reasoning is about whether a PREVIOUS APPROVAL exists to inherit a
        // date from, so it belongs on the enrolment axis. A mid-onboarding patient
        // regenerating their draft has never approved anything, so a stored date can only be
        // their own — restore it, exactly as the plain onboarding path does.
        //
        // PHA-1285: gating on isFirstTimeEnrollment also dropped the date the clinician set
        // in THIS update draft: leave the step (or Save Draft & Exit) and come back, and the
        // field was back on the default, which approve then stored. The server now returns
        // the date only when it belongs to the draft in progress (get_care_plan_draft_meta),
        // so a stale one never arrives and the gate is gone. Never overwrite a date already typed in
        // this mount — the fetch can resolve after the clinician started editing.
        if (meta.expiration_date && !expirationDateTouchedRef.current) {
          // PHA-1080: a restored draft date is the clinician's, not a default -- pin it so
          // a late-resolving clinic cycle cannot overwrite their saved work.
          expirationDateTouchedRef.current = true;
          setExpirationDate(meta.expiration_date);
        }
        if (meta.consent_status === 'yes' || meta.consent_status === 'no') {
          setConsentStatus(meta.consent_status);
        }
      } catch (err) {
        console.error('Failed to load draft meta:', err);
      } finally {
        if (!cancelled) setDraftMetaLoaded(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboarding, patientId]);

  // Capture the clean baseline once the plan has hydrated + meta applied; thereafter track dirty.
  useEffect(() => {
    if (!isOnboarding) return;
    if (baselineKeyRef.current === null) {
      if (draftMetaLoaded && carePlanGenerated && editableCarePlans.length > 0) {
        baselineKeyRef.current = stateKey;
        lastSavedKeyRef.current = stateKey;
        setHasUnsavedChanges(false);
        onUnsavedChangesChange?.(false);
      }
      return;
    }
    // PHA-896: dirty is measured against what's SAVED (lastSaved), not the entry baseline —
    // otherwise a successful "Save Draft" still looks unsaved and prompts on exit. Discard
    // still reverts to baselineKeyRef; that's a separate concern.
    const dirty = stateKey !== (lastSavedKeyRef.current ?? baselineKeyRef.current);
    setHasUnsavedChanges(dirty);
    onUnsavedChangesChange?.(dirty);
    // PHA-1285: draftMetaLoaded is state, not a ref. As a ref, a meta fetch that resolved
    // after the plan hydrated re-rendered nothing, so the baseline was only captured on the
    // clinician's FIRST edit — absorbing that edit into the "clean" state, which autosave
    // then never sent.
  }, [stateKey, isOnboarding, carePlanGenerated, editableCarePlans.length, onUnsavedChangesChange, draftMetaLoaded]);

  // Debounced autosave so edits survive tab switches / logins without an explicit Save click.
  useEffect(() => {
    if (!isOnboarding || baselineKeyRef.current === null) return;
    if (stateKey === (lastSavedKeyRef.current ?? baselineKeyRef.current)) return;
    const handle = window.setTimeout(() => {
      persistDraft(stateKeyRef.current).catch(err => console.error('Autosave failed:', err));
    }, 1500);
    return () => window.clearTimeout(handle);
  }, [stateKey, isOnboarding, persistDraft]);

  // PHA-1285: leaving the step inside the 1.5s debounce cleared the timer above and the edit
  // never reached the server, so coming back showed the default review date again. Flush
  // whatever is still unsaved when the step unmounts — except on the exits that end the
  // draft server-side (Discard regeneration, Exit Onboarding on declined consent), where a
  // flush would write the draft back after it was discarded.
  const skipExitFlushRef = useRef(false);
  useEffect(() => { skipExitFlushRef.current = false; }, [stateKey]);
  useEffect(() => {
    if (!isOnboarding) return;
    return () => {
      const base = baselineKeyRef.current;
      if (skipExitFlushRef.current) return;
      if (base === null || stateKeyRef.current === (lastSavedKeyRef.current ?? base)) return;
      persistDraft(stateKeyRef.current).catch(err => console.error('Autosave on exit failed:', err));
    };
  }, [isOnboarding, persistDraft]);

  // Register stable Save / Discard handlers so the Dashboard exit modal can drive them.
  useEffect(() => {
    if (!isOnboarding) return;
    registerDraftControls?.({
      save: async () => {
        await persistDraft(stateKeyRef.current);
        toast.success('Draft saved successfully.');
      },
      discard: async () => {
        const base = baselineKeyRef.current;
        if (!base) return;
        const snap = JSON.parse(base);
        setEditableCarePlans(snap.editableCarePlans);
        setQuestionFrequencyOverrides(snap.questionFrequencyOverrides);
        setGeneralQuestions(snap.generalQuestions);
        setConsentStatus(snap.consentStatus);
        expirationDateTouchedRef.current = true; // PHA-1080: baseline value, not a default
        setExpirationDate(snap.expirationDate);
        try {
          await patientApi.saveCarePlanDraft(
            patientId, snap.editableCarePlans, snap.questionFrequencyOverrides,
            snap.generalQuestions, snap.expirationDate, snap.consentStatus,
          );
        } catch (err) {
          console.error('Failed to revert draft:', err);
        }
        lastSavedKeyRef.current = base;
        setHasUnsavedChanges(false);
        onUnsavedChangesChange?.(false);
      },
    });
    return () => registerDraftControls?.(null);
  }, [isOnboarding, persistDraft, patientId, registerDraftControls, onUnsavedChangesChange]);

  // Clear the parent's unsaved flag when leaving the approve step (unmount).
  useEffect(() => () => { onUnsavedChangesChange?.(false); }, [onUnsavedChangesChange]);

  // PHA-895: hard-browser-close net (in-app tab switches use the Save/Discard modal).
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const loadDiseaseMatches = useCallback(async () => {
    if (!patientId || carePlanGenerated) return;
    setMatchLoading(true);
    setMatchError(null);
    try {
      // PHA-1157: a regeneration matches its staged review, so newly added Dx are offered.
      const result = await patientApi.matchCarePlanDiseases(patientId, !!isCarePlanRefresh);
      const matched: MatchedCarePlanDisease[] = result.matched_diseases || [];
      const suggest = !!result.suggest_general;
      setMatchedDiseases(matched);
      setSuggestGeneral(suggest);
      setMinQualifyingConditions(Number(result.min_qualifying_conditions) || 0);
      setRpmOneDxAllowed(result.rpm_one_dx_allowed === true);

      // PHA-1047: a refresh continues from the existing plan's focus diagnoses; a first-time
      // care plan starts from nothing (PHA-707).
      setSelectedDiseases(
        refreshPreselectDiseases
          ? getRefreshInitialSelectedDiseases(matched, suggest, refreshPreselectDiseases)
          : getInitialSelectedDiseases(matched, suggest)
      );

      // PHA-1036: this now always returns null — the picker is never skipped. Kept as a
      // call rather than deleted so the behaviour lives in one place.
      const autoDiseases = getAutoGenerateCarePlanDiseases(matched, suggest);
      if (autoDiseases) {
        const autoKey = `${patientId}:${autoDiseases.join('|')}`;
        if (autoGenerateKeyRef.current !== autoKey) {
          autoGenerateKeyRef.current = autoKey;
          try {
            await onGenerateCarePlan(autoDiseases);
          } catch (error) {
            autoGenerateKeyRef.current = null;
            console.error('Care plan auto-generation failed:', error);
          }
        }
      } else {
        autoGenerateKeyRef.current = null;
      }
    } catch (err: any) {
      setMatchError(err.message || 'Failed to match conditions to care plan templates');
      setMatchedDiseases([]);
      setSuggestGeneral(false);
      setMinQualifyingConditions(0);
      setSelectedDiseases(new Set());
    } finally {
      setMatchLoading(false);
    }
  }, [patientId, carePlanGenerated, onGenerateCarePlan, refreshPreselectDiseases, isCarePlanRefresh]);

  useEffect(() => {
    autoGenerateKeyRef.current = null;
    loadDiseaseMatches();
  }, [loadDiseaseMatches, reviewChangedSinceGeneration, patientId]);

  const toggleDiseaseSelection = (displayName: string, checked: boolean) => {
    setSelectedDiseases((prev) => {
      const next = new Set(prev);
      if (checked) next.add(displayName);
      else next.delete(displayName);
      return next;
    });
  };

  const selectedDiseasesList = Array.from(selectedDiseases);
  // PHA-1157: the conditions the last generation actually used. Re-generate and "Reset to
  // standard guidelines" resend these, not the checkboxes, which the match loader can re-seed
  // while a generation runs (see resolveRegenerateSelection).
  const lastGenerationSelectionRef = useRef<string[] | null>(null);
  const ccmGenerateBlock = getCcmGenerateBlock(
    matchedDiseases,
    selectedDiseases,
    minQualifyingConditions,
    rpmOneDxAllowed
  );

  const generationSelectedDiseases = useMemo(
    () => resolveGenerationSelectedDiseases(selectedDiseasesList, editableCarePlans),
    [selectedDiseasesList, editableCarePlans]
  );

  const runGenerate = async (regenerate: boolean) => {
    if (selectedDiseasesList.length === 0) {
      showError({
        kind: 'notice',
        title: 'No conditions selected',
        error: 'Select at least one condition before generating a care plan.',
      });
      return;
    }
    if (!regenerate && ccmGenerateBlock) {
      showError(CCM_ELIGIBILITY_NOTICE);
      return;
    }
    lastGenerationSelectionRef.current = selectedDiseasesList;
    if (regenerate && onRegenerateCarePlan) {
      await onRegenerateCarePlan(selectedDiseasesList);
    } else {
      await onGenerateCarePlan(selectedDiseasesList);
    }
  };

  React.useEffect(() => {
    if (carePlanData && carePlanGenerated) {
      setEditableCarePlans(buildEditableCarePlans(carePlanData.care_plan_definitions, patientId, approvedProblems));
    }
  }, [carePlanData, carePlanGenerated, patientId, approvedProblems]);

  // --- Scroll-spy ---

  const scrollAreaRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const isScrollingToSection = useRef(false);

  const getActiveTab = (planIndex: number): TabName =>
    (activeTabs[planIndex] as TabName) ?? 'goals';

  const setActiveTabForPlan = useCallback((planIndex: number, tab: TabName) => {
    setActiveTabs(prev => prev[planIndex] === tab ? prev : { ...prev, [planIndex]: tab });
  }, []);

  const handleScrollSpy = (planIndex: number) => {
    if (isScrollingToSection.current) return;
    const scrollEl = scrollAreaRefs.current[planIndex];
    if (!scrollEl) return;

    const scrollTop = scrollEl.scrollTop;
    let activeSection: TabName = 'goals';

    for (const tab of TAB_ORDER) {
      const sectionEl = sectionRefs.current[`${planIndex}-${tab}`];
      if (sectionEl && sectionEl.offsetTop <= scrollTop + 10) {
        activeSection = tab;
      }
    }

    setActiveTabForPlan(planIndex, activeSection);
  };

  const handleTabClick = (planIndex: number, tab: TabName) => {
    setActiveTabForPlan(planIndex, tab);
    const sectionEl = sectionRefs.current[`${planIndex}-${tab}`];
    const scrollEl = scrollAreaRefs.current[planIndex];
    if (sectionEl && scrollEl) {
      isScrollingToSection.current = true;
      scrollEl.scrollTop = sectionEl.offsetTop;
      requestAnimationFrame(() => { isScrollingToSection.current = false; });
    }
  };

  // --- Item CRUD ---

  const handleAddItem = (type: ItemType, planIndex: number) => {
    const plan = editableCarePlans[planIndex];
    const base = { patient_id: patientId, disease: plan.disease, uploaded_ts: plan.uploaded_ts, approved: true };

    // PHA-1131: due_date defaults to null, not '' — the backend's due_date field is
    // Optional[date] and treats an unset value as None. An empty string used to survive
    // untouched into the approve payload (most reliably for orgs with
    // no_target_dates_enabled, where the Due Date editor below never even renders to let
    // it be corrected) and 400 the whole care-plan approval.
    const newItem = type === 'goal'
      ? { measure: '', target_value: '', test: '', due_date: null, goal_index: (plan.goals.length + 1).toString(), ...base }
      : type === 'question'
      ? { prompt: '', frequency: 'daily', data_type: 'str', question_index: (plan.questions.length + 1).toString(), ...base }
      : { task: '', assignee: 'patient', frequency: '', due_date: null, task_index: (plan.tasks.length + 1).toString(), ...base };

    setEditDialog({ open: true, type, item: newItem, isNew: true, planIndex });
  };

  const handleEditItem = (type: ItemType, item: any, planIndex: number) => {
    // Normalize legacy data_type labels to enum values
    const normalizedItem = { ...item };
    if (type === 'question' && normalizedItem.data_type) {
      const dt = normalizedItem.data_type.toLowerCase().trim();
      const dataTypeMap: Record<string, string> = {
        'yes/no': 'bool', 'boolean': 'bool', 'true/false': 'bool',
        'text': 'str', 'string': 'str',
        'number (whole)': 'int', 'integer': 'int', 'number': 'int', 'whole number': 'int',
        'number (decimal)': 'float', 'decimal': 'float', 'decimal number': 'float',
        'blood pressure': 'blood pressure', 'bp': 'blood pressure',
        'hospitalization': 'hospitalization',
        'single choice': 'single_choice', 'single_choice': 'single_choice', 'radio': 'single_choice',
        'date': 'date', 'date & time': 'datetime', 'datetime': 'datetime',
        'str': 'str', 'int': 'int', 'float': 'float', 'bool': 'bool',
      };
      normalizedItem.data_type = dataTypeMap[dt] || normalizedItem.data_type;
    }
    setEditDialog({ open: true, type, item: normalizedItem, isNew: false, planIndex });
  };

  const handleSaveItem = () => {
    const { type, item, isNew, planIndex } = editDialog;
    if (!type || !item) return;

    // General section (planIndex = -1)
    if (planIndex === -1 && type === 'question') {
      if (isNew) {
        setGeneralQuestions(prev => [...prev, { ...item, question_index: prev.length }]);
      } else {
        setGeneralQuestions(prev =>
          prev.map(q => q.question_index === item.question_index ? { ...item } : q)
        );
      }
    } else {
      setEditableCarePlans(prev => prev.map((plan, i) => {
        if (i !== planIndex) return plan;

        if (type === 'goal') {
          return {
            ...plan,
            goals: isNew
              ? [...plan.goals, item]
              : plan.goals.map(g => g.goal_index === item.goal_index ? item : g)
          };
        } else if (type === 'question') {
          return {
            ...plan,
            questions: isNew
              ? [...plan.questions, item]
              : plan.questions.map(q => q.question_index === item.question_index ? item : q)
          };
        } else {
          return {
            ...plan,
            tasks: isNew
              ? [...plan.tasks, item]
              : plan.tasks.map(t => t.task_index === item.task_index ? item : t)
          };
        }
      }));
    }

    // PHA-406 V2: Track frequency overrides for questions
    if (type === 'question' && item.prompt && item.frequency) {
      setQuestionFrequencyOverrides(prev => ({
        ...prev,
        [item.prompt]: item.frequency
      }));
    }

    setEditDialog(EDIT_DIALOG_CLOSED);
  };

  const handleDeleteItem = (type: ItemType, index: number, planIndex: number) => {
    setEditableCarePlans(prev => prev.map((plan, i) => {
      if (i !== planIndex) return plan;
      if (type === 'goal') return { ...plan, goals: plan.goals.filter((_, j) => j !== index) };
      if (type === 'question') return { ...plan, questions: plan.questions.filter((_, j) => j !== index) };
      return { ...plan, tasks: plan.tasks.filter((_, j) => j !== index) };
    }));
  };

  // --- ICD-10 CRUD ---

  const handleAddICD10Code = (code: ICD10Code, planIndex: number) => {
    setEditableCarePlans(prev => prev.map((plan, i) => {
      if (i !== planIndex || plan.icd10_codes.some(c => c.code === code.code)) return plan;
      return { ...plan, icd10_codes: [...plan.icd10_codes, code] };
    }));
  };

  const handleRemoveICD10Code = (codeToRemove: string, planIndex: number) => {
    setEditableCarePlans(prev => prev.map((plan, i) => {
      if (i !== planIndex) return plan;
      return { ...plan, icd10_codes: plan.icd10_codes.filter(c => c.code !== codeToRemove) };
    }));
  };

  // --- PHA-737: Print Draft ---

  const handlePrintDraft = async () => {
    if (!patientForPdf) {
      toast.error('Patient information not available for PDF generation.');
      return;
    }
    // Merge patient_friendly_plan from the source carePlanData definitions (by disease+uploaded_ts)
    // because editableCarePlans (EditableCarePlanDefinition) does not carry that field — it is
    // intentionally omitted to avoid influencing the approve payload.
    const carePlanDefsWithNarrative = editableCarePlans.map((plan) => (
      { ...plan, patient_friendly_plan: patientFriendlyPlanFor(plan) }
    ));
    // PHA-737: patients.apcm_tier is NULL until approval, so it can't be read for the draft PDF's
    // program label — that's why the draft showed "CCM" while the final showed "APCM". Compute the
    // prospective tier (what approval WOULD set) from the draft tables on click. Awaiting here (vs a
    // pre-loaded effect) avoids the race where Print Draft is clicked before the value resolves.
    const pid = patientForPdf.patient_id || patientId;
    let prospectiveApcmTier = patientForPdf.apcm_tier ?? null;
    // PHA-1290: the patient's Care Plan Notes, fetched on click. A patient can already have notes
    // while a draft is open (added from the chart before approving), so this is never assumed empty.
    const carePlanReviewsPromise = fetchCarePlanReviewsForPdf(pid);
    try {
      prospectiveApcmTier = await patientApi.getDraftApcmTier(pid);
    } catch (err) {
      console.error('PHA-737: failed to fetch prospective APCM tier; falling back to persisted value:', err);
    }
    const carePlanReviews = await carePlanReviewsPromise;
    try {
      await generateCarePlanPdf({
        ...patientForPdf,
        first_name: patientForPdf.first_name,
        last_name: patientForPdf.last_name,
        date_of_birth: patientForPdf.date_of_birth,
        patient_id: pid,
        // PHA-737: prospective program label — matches the final post-approval PDF.
        apcm_tier: prospectiveApcmTier,
        // PHA-1032: not yet approved — DRAFT banner/header/filename, and the current user
        // stands in for the (nonexistent) approving clinician.
        is_draft: true,
        draft_reviewer_name: user?.name || user?.email || null,
        // PHA-895 follow-up: pass the org's no-target-dates flag + reviews so this draft PDF
        // renders identically to the patient-chart "Download Care Plan" PDF (which passes both).
        no_target_dates_enabled: noTargetDatesEnabled,
        care_plan_reviews: carePlanReviews,
        // PHA-737: drafts have no stored expiration; forward the on-screen value (defaults to today+6mo).
        expiration_date: expirationDate ? maskedDateToIso(expirationDate) : null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        care_plan_definitions: carePlanDefsWithNarrative as any, // PHA-737: edited/draft defs (nullable frequency etc.) bridge to the PDF util's local CarePlanDefinition shape
      });
    } catch (err) {
      console.error('Failed to generate draft PDF:', err);
      toast.error('Failed to generate PDF. Please try again.');
    }
  };

  // --- PHA-626: Onboarding Script ---

  const handleOpenOnboardingScript = async () => {
    if (!patientForPdf) return;
    // Same prospective-tier lookup as Print Draft: patients.apcm_tier is NULL until approval.
    const pid = patientForPdf.patient_id || patientId;
    let tier = patientForPdf.apcm_tier ?? null;
    try {
      tier = await patientApi.getDraftApcmTier(pid);
    } catch (err) {
      console.error('PHA-626: failed to fetch prospective APCM tier; falling back to persisted value:', err);
    }
    // The clinic's own wording, as on the chart. The wizard runs under the active clinic;
    // 'ALL' (an internal user's view) is no clinic, so it gets the built-in script.
    let template = null;
    if (orgId && orgId !== 'ALL') {
      try {
        template = normalizeOnboardingScriptTemplate(
          (await patientApi.getOnboardingScriptTemplate(String(orgId)))?.template,
        );
      } catch (err) {
        console.error('PHA-626: failed to load the clinic onboarding script; using the built-in one:', err);
      }
    }
    const defsWithNarrative = editableCarePlans.map((plan) => (
      { ...plan, patient_friendly_plan: patientFriendlyPlanFor(plan) }
    ));
    const result = buildPatientOnboardingScript({
      patient: { ...patientForPdf, apcm_tier: tier },
      draftDefinitions: defsWithNarrative,
      draftApcmTier: tier,
      template,
      clinicName: patientForPdf.organization_name,
    });
    setOnboardingScriptNotice(result.notice);
    setOnboardingScript(result.script);
  };

  // --- Approval ---

  // PHA-967: explicit "Save as Draft" — persist immediately (rather than waiting out the 1.5s
  // autosave debounce) and hand control back to the host, which leaves the wizard and opens the
  // patient's chart on Care Plans. Deliberately NOT gated on consent or ICD-10 completeness:
  // those are approval requirements, and a draft is by definition still incomplete.
  const handleSaveDraftAndExit = async () => {
    setSavingDraft(true);
    try {
      await persistDraft(stateKeyRef.current);
      toast.success('Draft saved.');
      onSaveDraftAndExit?.();
    } catch (error: any) {
      console.error('Failed to save care plan draft:', error);
      if (error?.errorData) {
        showError(error.errorData);
      } else {
        toast.error('Failed to save the draft. Please try again.');
      }
    } finally {
      setSavingDraft(false);
    }
  };

  const handleApproveCarePlan = async () => {
    const plansMissingICD10 = editableCarePlans
      .filter(plan => plan.icd10_codes.length === 0)
      .map(plan => plan.disease);
    if (plansMissingICD10.length > 0) {
      toast.error(
        `Each care plan must have at least one ICD-10 code before approval. Missing: ${plansMissingICD10.join(', ')}`
      );
      return;
    }

    if (voiceOfPatientMissing) {
      toast.error(VOICE_OF_PATIENT_REQUIRED_MESSAGE);
      vopSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // Validate expiration date before proceeding (field holds MM/DD/YYYY).
    // PHA-1285: required. An empty field used to skip the update below and silently leave the
    // server's approval + review-cycle fallback in place, i.e. "it saved as the default".
    const expirationIso = expirationDate ? maskedDateToIso(expirationDate) : null;
    if (!expirationIso || !isFutureMaskedDate(expirationDate)) {
      toast.error('Please enter a valid future review date (MM/DD/YYYY).');
      return;
    }

    setApproving(true);

    try {
      // PHA-895: in onboarding, defer survey generation to the completion step so patients are
      // not surveyed before they are fully registered. approve(deferSurveys=true) returns no
      // surveys; completeOnboarding records consent AND generates them. Existing-patient
      // re-approval generates inline and reads surveys straight from the approve response.
      // PHA-897/PHA-726: a REFRESH must never reach completeOnboarding. That call records
      // consent, and patients.consent_date is the one-time enrollment date the billing
      // report reads (PHA-953) — re-writing it on every care-plan update is the bug this
      // ticket exists for. A refresh therefore generates surveys inline (deferSurveys=false)
      // and records an acknowledgment instead.
      // PHA-1119: "already enrolled" is the actual condition, not "is a regeneration". A
      // patient who is mid-onboarding and regenerating their draft has no consent_date to
      // overwrite and no enrolment yet — completeOnboarding is what creates both, so they
      // take the onboarding branch even though the wizard is running in regeneration mode.
      const deferSurveys = isOnboarding && isFirstTimeEnrollment;
      const response = await patientApi.approveCarePlanDefinitions(
        patientId, editableCarePlans, questionFrequencyOverrides, generalQuestions, deferSurveys,
      );

      // PHA-1119: the acknowledgment records "I reviewed this UPDATE with the patient", and
      // it is the checkbox above it that the clinician actually ticked. A first-time
      // enrolment shows consent radios instead and never ticks it, so writing an
      // acknowledgment row here would assert a review that was never acknowledged.
      if (!isFirstTimeEnrollment) {
        // Best-effort: the plan is already approved by this point, so a failure here must
        // not present as a failed approval. It is a missing audit row, and the chart shows
        // its absence honestly rather than claiming a review that did not happen.
        try {
          // The display name is resolved server-side from the session user, the same way
          // PHA-867 snapshots reviewed_by_name — this component has no user context.
          await patientApi.recordCarePlanAcknowledgment(
            patientId,
            editableCarePlans[0]?.uploaded_ts ?? null,
          );
        } catch (ackErr) {
          console.error('PHA-897: failed to record care plan acknowledgment:', ackErr);
        }
      }

      let rawSurveys: any[] = [];
      if (deferSurveys) {
        const completeResp = await patientApi.completeOnboarding(
          patientId,
          consentStatus === 'yes' ? 'collected' : 'denied',
          questionFrequencyOverrides,
          generalQuestions,
        );
        if (completeResp?.surveys && Array.isArray(completeResp.surveys)) {
          rawSurveys = completeResp.surveys;
        }
      } else if (response.surveys && Array.isArray(response.surveys)) {
        rawSurveys = response.surveys;
      }

      const surveys: SurveyResult[] = [];
      if (rawSurveys.length > 0) {
        const uniqueSurveys = Array.from(
          new Map(rawSurveys.flat().map((s: any) => [s.survey_id, s])).values()
        );
        uniqueSurveys.forEach((survey: any) => {
          surveys.push({
            name: survey.name || 'Survey',
            frequency: survey.frequency || 'daily',
            url: survey.url || `/survey/${survey.survey_id}`
          });
        });
      }

      // PHA-895: the plan is now approved (+ onboarding finalized). Mark it clean so leaving the
      // approve step doesn't trip the unsaved-changes prompt.
      baselineKeyRef.current = stateKeyRef.current;
      lastSavedKeyRef.current = stateKeyRef.current;
      setHasUnsavedChanges(false);
      onUnsavedChangesChange?.(false);

      const surveysWithLinks = await Promise.all(
        surveys.map(async (survey) => {
          try {
            const surveyId = survey.url.split('/survey/')[1]?.split('/')[0];
            if (surveyId) {
              const linkResponse = await patientApi.createSurveyLink(surveyId, patientId);
              return { ...survey, link: linkResponse.url || survey.url };
            }
            return survey;
          } catch (error) {
            console.error('Failed to create survey link:', error);
            return survey;
          }
        })
      );

      // If an expiration date was set, update all approved plans before navigating away
      let expirationSaveFailed = false;
      if (expirationIso) {
        const expirationResults = await Promise.allSettled(
          editableCarePlans.map(plan =>
            patientApi.updateCarePlanExpiration(patientId, plan.disease, plan.uploaded_ts, expirationIso)
          )
        );
        expirationSaveFailed = expirationResults.some(r => r.status === 'rejected');
        if (expirationSaveFailed) {
          expirationResults.forEach((r, i) => {
            if (r.status === 'rejected') {
              console.error('Failed to set expiration date for care plan:', editableCarePlans[i]?.disease, r.reason);
            }
          });
        }
      }

      if (expirationSaveFailed) {
        toast.error('Care plan approved, but the review date could not be saved.');
      }

      // PHA-1204: clinics with automatic upload get the new plan filed into the EHR now, while
      // the clinician is still here to see whether it worked. Runs AFTER the review-date write
      // above, because the filed PDF prints that date. The approval already stands, so an
      // upload failure is reported as exactly that and never as a failed approval.
      setUploadingToEhr(true);
      try {
        const upload = await autoPushCarePlanToEhr(patientId, { organizationId: orgId });
        if (upload.status === 'submitted' || upload.status === 'already_sent') {
          // PHA-1212: submitted, not filed — the EHR's verdict arrives by webhook and shows in
          // the Care Plans tab. One message, which also says the plan was created, rather
          // than an "approved" toast stacked on an upload toast.
          toast.success(
            `${AUTO_PUSH_SUBMITTED_TITLE}\n\n${AUTO_PUSH_SUBMITTED_DETAIL}`,
            { duration: 10000, style: { whiteSpace: 'pre-line' } },
          );
        } else if (upload.status === 'skipped') {
          toast.success('Care plan approved successfully!');
        } else if (upload.status === 'failed') {
          toast.error(
            `${AUTO_PUSH_FAILURE_TITLE}\n\n${AUTO_PUSH_FAILURE_DETAIL}`,
            { duration: 10000, style: { whiteSpace: 'pre-line' } },
          );
        }
      } finally {
        setUploadingToEhr(false);
      }

      onApprove(surveysWithLinks, consentStatus === 'yes');
    } catch (error: any) {
      console.error('Failed to approve care plan:', error);
      if (error.errorData) {
        showError(approvalRefusalNotice(error.errorData));
      } else {
        toast.error('Failed to approve care plan. Please try again.');
      }
    }

    setApproving(false);
  };

  // --- Section Renderers ---


  const renderEditableGoals = (goals: EditableCarePlanGoal[], planIndex: number) => (
    <Box>
      <Flex justify="between" align="center" className="mb-4">
        <Text size="3" weight="medium" className="text-slate-700">Clinical Goals & Targets</Text>
        <Button size="2" onClick={() => handleAddItem('goal', planIndex)}>
          <PlusIcon width="14" height="14" />
          Add Goal
        </Button>
      </Flex>

      <Card className="overflow-hidden">
        <Table.Root>
          <Table.Header>
            <Table.Row className="bg-slate-50">
              <Table.ColumnHeaderCell style={{ width: '20%' }}>Measure</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell style={{ width: '25%' }}>Target Value</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell style={{ width: '25%' }}>Test/Method</Table.ColumnHeaderCell>
              {/* PHA-866: hide the Due Date column when the org disables target dates */}
              {!noTargetDatesEnabled && (
                <Table.ColumnHeaderCell style={{ width: '15%' }}>Due Date</Table.ColumnHeaderCell>
              )}
              <Table.ColumnHeaderCell style={{ width: '15%' }}>Actions</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {goals.map((goal, index) => {
              return (
              <Table.Row key={index} className="hover:bg-slate-50">
                <Table.Cell>
                  <Text weight="medium" size="2">{goal.measure}</Text>
                  {/* PHA-1142: the reading this goal's target was calculated from.
                      PHA-1157: a regeneration keeps the previous BP / BMI target; when a newer
                      reading gives a different one, it is offered here for the clinician to adopt. */}
                  {(() => {
                    const recalculated = recalculatedTargetFor(goal.measure, targetBaseline);
                    const adopted = !!recalculated && sameTarget(recalculated.target, goal.target_value);
                    const calculatedFrom = describeBaselineForGoal(
                      goal.measure,
                      adopted ? recalculated!.readings : targetBaseline,
                    );
                    return (
                      <>
                        {calculatedFrom && (
                          <Text size="1" color="gray" as="div" style={{ marginTop: 2 }}>
                            Target calculated from {calculatedFrom}
                          </Text>
                        )}
                        {recalculated && !adopted && (
                          <Flex align="center" gap="2" wrap="wrap" style={{ marginTop: 4 }} data-testid="recalculated-target">
                            <Text size="1" color="blue">
                              Recalculated from {describeBaselineForGoal(goal.measure, recalculated.readings) || 'the latest reading'}:{' '}
                              <Text weight="bold">{String(recalculated.target)}</Text>
                            </Text>
                            <Button
                              size="1"
                              variant="soft"
                              onClick={() => setEditableCarePlans(prev => prev.map((p, i) => i !== planIndex ? p : {
                                ...p,
                                goals: p.goals.map((g, gi) => gi !== index ? g : { ...g, target_value: String(recalculated.target) }),
                              }))}
                            >
                              Use this target
                            </Button>
                          </Flex>
                        )}
                      </>
                    );
                  })()}
                  {/* PHA-510 root-cause display: italic note shown only when
                      source_change_reason is populated by the EHR cron. */}
                  {goal.source_change_reason && (
                    <Text
                      size="1"
                      color="amber"
                      as="div"
                      style={{ fontStyle: 'italic', marginTop: 2 }}
                    >
                      {goal.source_change_reason}
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell style={{ maxWidth: 0 }}>
                  {goal.target_value
                    ? <Tooltip content={goal.target_value}>
                        <Badge
                          color="green"
                          variant="soft"
                          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', display: 'inline-block' }}
                        >
                          {goal.target_value}
                        </Badge>
                      </Tooltip>
                    : <Text size="2" className="text-slate-500">No specific target</Text>}
                </Table.Cell>
                <Table.Cell><Text size="2">{goal.test}</Text></Table.Cell>
                {!noTargetDatesEnabled && (
                  <Table.Cell><Text size="2">{formatDate(goal.due_date)}</Text></Table.Cell>
                )}
                <Table.Cell>
                  <Flex gap="1">
                    <IconButton size="1" variant="ghost" onClick={() => handleEditItem('goal', goal, planIndex)}>
                      <Pencil1Icon width="16" height="16" />
                    </IconButton>
                    <IconButton size="1" variant="ghost" color="red" onClick={() => handleDeleteItem('goal', index, planIndex)}>
                      <TrashIcon width="16" height="16" />
                    </IconButton>
                  </Flex>
                </Table.Cell>
              </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Card>
    </Box>
  );

  const renderEditableQuestions = (questions: EditableCarePlanQuestion[], planIndex: number) => (
    <Box>
      <Flex justify="between" align="center" className="mb-4">
        <Text size="3" weight="medium" className="text-slate-700">Patient Monitoring Questions</Text>
        <Button size="2" onClick={() => handleAddItem('question', planIndex)}>
          <PlusIcon width="14" height="14" />
          Add Question
        </Button>
      </Flex>
      <Box className="space-y-3">
        {questions.filter(q => !q.conditional).map((question, index) => (
          <Card key={index} className="p-4">
            <Flex justify="between" align="start" gap="4">
              <Box className="flex-1">
                <Text size="3" weight="medium" className="mb-2 block">{question.prompt}</Text>
                {question.prompt_english && (
                  <Text size="2" color="gray" style={{ fontStyle: 'italic' }} className="mb-2 block">
                    English: {question.prompt_english}
                  </Text>
                )}
                {(() => {
                  // PHA-815: read the backend-computed cadence (single source of truth),
                  // honoring any in-session clinician override on question.frequency.
                  const cadence = cronToCadence(resolveQuestionCadence(question));
                  const badgeColor = cadence ? getCadenceBadgeColor(cadence.cadence_type) : 'gray';
                  return <Badge color={badgeColor as any} variant="soft" size="1">{formatCadenceBadge(cadence)}</Badge>;
                })()}
              </Box>
              <Flex gap="1">
                <IconButton size="1" variant="ghost" onClick={() => handleEditItem('question', question, planIndex)}>
                  <Pencil1Icon width="16" height="16" />
                </IconButton>
                <IconButton size="1" variant="ghost" color="red" onClick={() => handleDeleteItem('question', index, planIndex)}>
                  <TrashIcon width="16" height="16" />
                </IconButton>
              </Flex>
            </Flex>
          </Card>
        ))}
      </Box>
    </Box>
  );

  const renderEditableTasks = (tasks: EditableCarePlanTask[], planIndex: number) => {
    const indexed = tasks.map((task, i) => ({ task, originalIndex: i }));
    const clinicianTasks = indexed.filter(t => (t.task.assignee || '').toLowerCase() !== 'patient');
    const patientTasks = indexed.filter(t => (t.task.assignee || '').toLowerCase() === 'patient');

    const renderTaskCard = ({ task, originalIndex }: { task: EditableCarePlanTask; originalIndex: number }) => {
      const freq = formatFrequency(task.frequency);
      const showFrequency = task.frequency && freq !== 'N/A' && freq !== 'custom schedule';

      return (
        <Card key={originalIndex} className="p-4">
          <Flex justify="between" align="start" gap="4">
            <Box className="flex-1">
              {showFrequency && (
                <Flex align="center" gap="2" className="mb-2">
                  <Badge color="green" variant="soft" size="1">{freq}</Badge>
                </Flex>
              )}
              <Text size="3" weight="medium" className="mb-2 block">{task.task}</Text>
              {/* PHA-510 root-cause display — see EditableCarePlanGoal note. */}
              {task.source_change_reason && (
                <Text
                  size="1"
                  color="amber"
                  as="div"
                  style={{ fontStyle: 'italic', marginTop: -4, marginBottom: 6 }}
                >
                  {task.source_change_reason}
                </Text>
              )}
              {/* PHA-866: org hides target dates ⇒ always show "Due: N/A" */}
              {noTargetDatesEnabled ? (
                <Flex align="center" gap="1" className="mt-2">
                  <CalendarIcon width="12" height="12" className="text-slate-400" />
                  <Text size="2" className="text-slate-500">Due: N/A</Text>
                </Flex>
              ) : task.due_date && (
                <Flex align="center" gap="1" className="mt-2">
                  <CalendarIcon width="12" height="12" className="text-slate-400" />
                  <Text size="2" className="text-slate-500">Due: {formatDate(task.due_date)}</Text>
                </Flex>
              )}
            </Box>
            <Flex gap="1">
              <IconButton size="1" variant="ghost" onClick={() => handleEditItem('task', task, planIndex)}>
                <Pencil1Icon width="16" height="16" />
              </IconButton>
              <IconButton size="1" variant="ghost" color="red" onClick={() => handleDeleteItem('task', originalIndex, planIndex)}>
                <TrashIcon width="16" height="16" />
              </IconButton>
            </Flex>
          </Flex>
        </Card>
      );
    };

    return (
      <Box>
        <Flex justify="between" align="center" className="mb-4">
          <Text size="3" weight="medium" className="text-slate-700">Care Plan Tasks</Text>
          <Button size="2" onClick={() => handleAddItem('task', planIndex)}>
            <PlusIcon width="14" height="14" />
            Add Task
          </Button>
        </Flex>
        <Box className="space-y-4">
          {clinicianTasks.length > 0 && (
            <Box>
              <Flex align="center" gap="2" style={{ padding: '6px 12px', background: '#e3f2fd', borderRadius: 6, borderLeft: '4px solid #1976d2', marginBottom: 8 }}>
                <Text weight="bold" size="2" style={{ color: '#1565c0' }}>Clinician Tasks</Text>
                <Badge color="blue" variant="solid" size="1">{clinicianTasks.length}</Badge>
              </Flex>
              <Box className="space-y-3">
                {clinicianTasks.map(renderTaskCard)}
              </Box>
            </Box>
          )}
          {patientTasks.length > 0 && (
            <Box>
              <Flex align="center" gap="2" style={{ padding: '6px 12px', background: '#e8f5e9', borderRadius: 6, borderLeft: '4px solid #388e3c', marginBottom: 8 }}>
                <Text weight="bold" size="2" style={{ color: '#2e7d32' }}>Patient Tasks</Text>
                <Badge color="green" variant="solid" size="1">{patientTasks.length}</Badge>
              </Flex>
              <Box className="space-y-3">
                {patientTasks.map(renderTaskCard)}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // --- Edit Dialog ---

  const renderEditDialog = () => {
    const { open, type, item, isNew } = editDialog;
    if (!open || !type || !item) return null;

    const updateField = (field: string, value: any) =>
      setEditDialog(prev => ({ ...prev, item: { ...prev.item, [field]: value } }));

    return (
      <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && setEditDialog(EDIT_DIALOG_CLOSED)}>
        <Dialog.Content style={{ maxWidth: 600 }}>
          <Dialog.Title>
            {isNew ? 'Add' : 'Edit'} {type.charAt(0).toUpperCase() + type.slice(1)}
          </Dialog.Title>

          <Box className="space-y-4 mt-4">
            {type === 'goal' && (
              <>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Measure</Text>
                  <TextField.Root>
                    <TextField.Input value={item.measure || ''} onChange={(e) => updateField('measure', e.target.value)} placeholder="e.g., HbA1c, Blood Pressure" />
                  </TextField.Root>
                </Box>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Target Value</Text>
                  <TextField.Root>
                    <TextField.Input value={item.target_value || ''} onChange={(e) => updateField('target_value', e.target.value)} placeholder="e.g., < 7%, 120/80 mmHg" />
                  </TextField.Root>
                </Box>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Test/Method</Text>
                  <TextField.Root>
                    <TextField.Input value={item.test || ''} onChange={(e) => updateField('test', e.target.value)} placeholder="e.g., Lab test, Blood pressure measurement" />
                  </TextField.Root>
                </Box>
                {/* PHA-866: hide the Due Date editor when the org disables target dates */}
                {!noTargetDatesEnabled && (
                  <Box>
                    <Text size="2" weight="medium" className="mb-2 block">Due Date</Text>
                    <TextField.Root>
                      {/* PHA-1131: a cleared date input fires onChange with '' — normalize to
                          null so the payload never carries the empty-string sentinel the
                          backend's due_date validator rejects. */}
                      <TextField.Input type="date" value={formatDate(item.due_date)} onChange={(e) => updateField('due_date', e.target.value || null)} />
                    </TextField.Root>
                  </Box>
                )}
              </>
            )}

            {type === 'question' && (
              <>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Question</Text>
                  <TextArea value={item.prompt || ''} onChange={(e) => updateField('prompt', e.target.value)} placeholder="Enter the question to ask the patient" rows={3} />
                </Box>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Frequency</Text>
                  {(() => {
                    // PHA-815: initialize the cadence controls from the resolved cadence
                    // (in-session override on item.frequency, else backend computed_cadence).
                    const resolved = cronToCadence(resolveQuestionCadence(item));
                    let cadenceType = resolved?.cadence_type || 'unscheduled';
                    let cadenceDay = resolved?.cadence_day ?? 1;
                    let cadenceHour = resolved?.cadence_time_hour ?? 9;
                    let cadenceMinute = resolved?.cadence_time_minute ?? 0;

                    const buildCron = (type: string, day: number, hour: number, minute: number) => {
                      if (type === 'unscheduled') return 'unscheduled';
                      if (type === 'daily') return `${minute} ${hour} * * *`;
                      return `${minute} ${hour} * * ${day}`;
                    };

                    return (
                      <Flex gap="2" align="center" wrap="wrap">
                        <Select.Root value={cadenceType} onValueChange={(v) => updateField('frequency', buildCron(v, cadenceDay, cadenceHour, cadenceMinute))}>
                          <Select.Trigger />
                          <Select.Content>
                            <Select.Item value="daily">Daily</Select.Item>
                            <Select.Item value="weekly">Weekly</Select.Item>
                            <Select.Item value="unscheduled">On-Demand</Select.Item>
                          </Select.Content>
                        </Select.Root>

                        {cadenceType === 'weekly' && (
                          <select
                            value={cadenceDay}
                            onChange={(e) => updateField('frequency', buildCron(cadenceType, parseInt(e.target.value), cadenceHour, cadenceMinute))}
                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--gray-6)', fontSize: '13px' }}
                          >
                            <option value="0">Sun</option>
                            <option value="1">Mon</option>
                            <option value="2">Tue</option>
                            <option value="3">Wed</option>
                            <option value="4">Thu</option>
                            <option value="5">Fri</option>
                            <option value="6">Sat</option>
                          </select>
                        )}

                        {cadenceType !== 'unscheduled' && (
                          <TimeInput12h
                            value={`${String(cadenceHour).padStart(2, '0')}:${String(cadenceMinute).padStart(2, '0')}`}
                            onChange={(v) => {
                              const [h, m] = v.split(':');
                              updateField('frequency', buildCron(cadenceType, cadenceDay, parseInt(h), parseInt(m)));
                            }}
                            style={{ fontSize: '13px' }}
                          />
                        )}
                      </Flex>
                    );
                  })()}
                </Box>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Answer Type</Text>
                  <Select.Root value={item.data_type || 'str'} onValueChange={(v) => {
                    updateField('data_type', v);
                    if (v === 'hospitalization') {
                      updateField('options', [{label: "No", value: "no"}, {label: "Yes", value: "yes"}]);
                      // PHA-823: hospitalization question is not mandatory for now.
                      updateField('required', false);
                    }
                  }} disabled={!!item._isInitialHospitalization}>
                    <Select.Trigger />
                    <Select.Content>
                      <Select.Item value="str">Text</Select.Item>
                      <Select.Item value="bool">Yes/No</Select.Item>
                      <Select.Item value="int">Number (whole)</Select.Item>
                      <Select.Item value="float">Number (decimal)</Select.Item>
                      <Select.Item value="blood pressure">Blood Pressure</Select.Item>
                      <Select.Item value="hospitalization">Hospitalization</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Box>
              </>
            )}

            {type === 'task' && (
              <>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Task Description</Text>
                  <TextArea value={item.task || ''} onChange={(e) => updateField('task', e.target.value)} placeholder="Describe the task to be completed" rows={3} />
                </Box>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Assignee</Text>
                  <Select.Root value={item.assignee || 'patient'} onValueChange={(v) => updateField('assignee', v)}>
                    <Select.Trigger />
                    <Select.Content>
                      <Select.Item value="patient">Patient</Select.Item>
                      <Select.Item value="clinician">Clinician</Select.Item>
                    </Select.Content>
                  </Select.Root>
                </Box>
                <Box>
                  <Text size="2" weight="medium" className="mb-2 block">Frequency</Text>
                  <TextField.Root>
                    <TextField.Input value={item.frequency || ''} onChange={(e) => updateField('frequency', e.target.value)} placeholder="e.g., daily, weekly, monthly" />
                  </TextField.Root>
                </Box>
                {/* PHA-866: hide the Due Date editor when the org disables target dates */}
                {!noTargetDatesEnabled && (
                  <Box>
                    <Text size="2" weight="medium" className="mb-2 block">Due Date</Text>
                    <TextField.Root>
                      {/* PHA-1131: same normalization as the goal editor above. */}
                      <TextField.Input type="date" value={formatDate(item.due_date)} onChange={(e) => updateField('due_date', e.target.value || null)} />
                    </TextField.Root>
                  </Box>
                )}
              </>
            )}
          </Box>

          <Flex gap="3" mt="6" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button onClick={handleSaveItem}>{isNew ? 'Add' : 'Save'}</Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    );
  };

  // --- Early Returns ---

  if (carePlanLoading) {
    return (
      <Card className="p-6">
        <TimedProgress
          active={carePlanLoading}
          title="Generating care plan"
          estimatedDurationMs={240000}
          steps={CARE_PLAN_PROGRESS_STEPS}
        />
      </Card>
    );
  }

  if (!carePlanGenerated || !editableCarePlans.length) {
    return (
      <Card className="p-6">
        <Heading size="5" className="text-slate-800 mb-1">Generate Care Plan</Heading>
        <Text className="text-slate-600 mb-4 block">
          Select the conditions to include. We matched approved problems to curated care plan templates.
          When more than one option is available, choose at least one before generating.
        </Text>

        {matchLoading && (
          <Flex align="center" gap="2" className="mb-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" aria-hidden />
            <Text className="text-slate-600">Matching conditions...</Text>
          </Flex>
        )}

        {matchError && (
          <Callout.Root color="red" className="mb-4">
            <Callout.Text>{matchError}</Callout.Text>
          </Callout.Root>
        )}

        {/* PHA-1199: a CCM-only clinic cannot create a plan with <2 qualifying conditions. */}
        {!matchLoading && !matchError && ccmGenerateBlock === 'chart' && (
          <Callout.Root color="red" className="mb-4" role="alert">
            <Callout.Text>
              <Text weight="bold" className="block">{CCM_ELIGIBILITY_TITLE}</Text>
              <Text className="block mt-1">{CCM_ELIGIBILITY_BODY}</Text>
              <Text className="block mt-1">{CCM_ELIGIBILITY_ACTION}</Text>
            </Callout.Text>
          </Callout.Root>
        )}

        {!matchLoading && !matchError && matchedDiseases.length === 0 && !suggestGeneral && ccmGenerateBlock !== 'chart' && (
          <Callout.Root color="amber" className="mb-4">
            <Callout.Text>
              No curated templates matched the approved problem list. Approve problems that indicate a supported condition, or contact support.
            </Callout.Text>
          </Callout.Root>
        )}

        {!matchLoading && (matchedDiseases.length > 0 || suggestGeneral) && (
          <Box className="mb-6 space-y-3">
            {matchedDiseases.map((d) => (
              <Text key={d.template_id} as="label" size="3">
                <Flex align="center" gap="2">
                  <Checkbox
                    checked={selectedDiseases.has(d.display_name)}
                    onCheckedChange={(checked) => toggleDiseaseSelection(d.display_name, checked === true)}
                  />
                  <Text>{d.display_name}</Text>
                  {/* PHA-1250: say which conditions don't count, or ticking one looks like it should. */}
                  {minQualifyingConditions > 0 && d.qualifying === false && (
                    <Text size="1" color="gray">(does not count toward CCM)</Text>
                  )}
                </Flex>
              </Text>
            ))}
            {ccmGenerateBlock === 'selection' && (
              <Text size="2" color="red" className="block">
                CCM requires at least two qualifying chronic conditions — select at least two
                {matchedDiseases.some((d) => d.qualifying === false) ? ' that count toward CCM.' : '.'}
              </Text>
            )}
            {suggestGeneral && (
              <Text as="label" size="3">
                <Flex align="center" gap="2">
                  <Checkbox
                    checked={selectedDiseases.has(GENERAL_CARE_PLAN_SELECTION_LABEL)}
                    onCheckedChange={(checked) =>
                      toggleDiseaseSelection(GENERAL_CARE_PLAN_SELECTION_LABEL, checked === true)
                    }
                  />
                  <Text>{GENERAL_CARE_PLAN_TITLE}</Text>
                </Flex>
              </Text>
            )}
          </Box>
        )}

        <Flex justify="end" gap="3">
          <Button
            size="3"
            variant="soft"
            onClick={() => loadDiseaseMatches()}
            disabled={matchLoading || carePlanLoading}
          >
            Refresh Matches
          </Button>
          <Button
            size="3"
            disabled={
              carePlanLoading ||
              matchLoading ||
              selectedDiseasesList.length === 0 ||
              (matchedDiseases.length === 0 && !suggestGeneral) ||
              ccmGenerateBlock !== null
            }
            onClick={async () => {
              try {
                await runGenerate(false);
              } catch (error) {
                console.error('Care plan generation failed:', error);
              }
            }}
          >
            Generate Care Plan
          </Button>
        </Flex>
      </Card>
    );
  }

  // --- Main Render ---

  const plansMissingICD10 = editableCarePlans
    .filter(plan => plan.icd10_codes.length === 0)
    .map(plan => plan.disease);
  const hasEmptyICD10 = plansMissingICD10.length > 0;

  return (
    <Card className="p-6">
      <Heading size="5" className="text-slate-800 mb-4">Review & Approve Care Plans</Heading>
      <Text size="3" className="text-slate-600 mb-8 block">
        Review and edit the care plans before approving. You can add, edit, or remove goals, questions, and tasks for each condition.
      </Text>

      {/* PHA-1157: a regeneration starts from the previous approved plan (its edits, its
          Voice of the Patient goals - those live on the patient, not in the draft - and the
          plain-English plan), with guideline content only for newly added conditions. This
          is the way back to the standard guidelines. Only offered when there IS a previous
          approved plan to have been the baseline. */}
      {isCarePlanRefresh && refreshHasApprovedPlan !== false && carePlanGenerated && onRegenerateCarePlan && (
        <Callout.Root color="blue" className="mb-6" data-testid="carryover-callout">
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            <Flex justify="between" align="center" gap="4">
              <Text>{describeCarryover(carePlanData?.carryover)}</Text>
              <Flex direction="column" gap="2" style={{ flexShrink: 0 }}>
                {/* PHA-1157: check the plain-English plan right from here - it is what the
                    patient reads, and only the print version shows it. */}
                {patientForPdf && (
                  <Button
                    size="2"
                    variant="soft"
                    className="whitespace-nowrap"
                    onClick={handlePrintDraft}
                  >
                    Open print version
                  </Button>
                )}
                <Button
                  size="2"
                  variant="soft"
                  className="whitespace-nowrap"
                  disabled={carePlanLoading || resettingToGuidelines}
                  onClick={() => setResetToGuidelinesOpen(true)}
                >
                  Reset to standard guidelines
                </Button>
              </Flex>
            </Flex>
          </Callout.Text>
        </Callout.Root>
      )}

      {/* PHA-945 put a second, blue framing of this callout on a RESUMED draft, so that
          re-opening an unfinished plan offered a re-generate without the clinician having to
          make a throwaway problem-list edit to arm the amber one.
          PHA-1119 removes that half. Re-generating from inside "Continue Onboarding" skipped
          the Upload and condition-selection steps — it regenerated against whatever was
          already on the chart, which is the bug this ticket was filed for. That entry point
          now lives on the chart as its own "Regenerate Care Plan" button (PatientDetail.tsx),
          which goes through the source picker and the full Upload → Review → conditions
          sequence. Continue Onboarding does exactly one thing again: resume where you were.
          The amber `reviewChangedSinceGeneration` prompt is untouched — it fires only after
          the clinician actually edits a problem or medication in THIS session, where
          re-generating against the current chart is precisely what they want. */}
      {reviewChangedSinceGeneration && (
        <Callout.Root color="amber" className="mb-6">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>
            <Flex justify="between" align="center">
              <Text>
                Activity was detected on the Review tab, if you changed the approved problems or medications it's better to re-generate a care plan
              </Text>
              <Button
                size="2"
                variant="solid"
                color="amber"
                className="ml-4 whitespace-nowrap"
                onClick={async () => {
                  try {
                    if (onRegenerateCarePlan) {
                      const diseasesToRegenerate = resolveRegenerateSelection(
                        lastGenerationSelectionRef.current,
                        selectedDiseasesList,
                        carePlanData?.care_plan_definitions,
                        matchedDiseases,
                        suggestGeneral
                      );
                      // PHA-908: an empty selection is legitimate when a plan already
                      // exists — it means "no explicit selection", so the server
                      // re-matches the current approved chart. Only block on the initial
                      // pick, where there is no plan to fall back to. Blocking here is
                      // what left care teams with no way out of the re-generate loop.
                      const hasExistingPlan =
                        (carePlanData?.care_plan_definitions?.length ?? 0) > 0;
                      if (diseasesToRegenerate.length === 0 && !hasExistingPlan) {
                        showError({
                          kind: 'notice',
                          title: 'No conditions selected',
                          error:
                            'Select at least one condition before re-generating a care plan.',
                        });
                        return;
                      }
                      await onRegenerateCarePlan(diseasesToRegenerate);
                    }
                  } catch (error) {
                    console.error('Care plan re-generation failed:', error);
                  }
                }}
                disabled={carePlanLoading}
              >
                {carePlanLoading ? 'Re-generating...' : 'Re-generate Care Plan'}
              </Button>
            </Flex>
          </Callout.Text>
        </Callout.Root>
      )}

      {editableCarePlans.map((plan, planIndex) => (
        <Box key={planIndex} className="mb-8">
          {/* Condition Header */}
          <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50">
            <Flex justify="between" align="center">
              <Box>
                <Heading size="4" className="text-slate-800 mb-1">{formatPatientCarePlanHeading(plan.disease, plan.diseases, plan.icd10_codes, plan.is_general)}</Heading>
                <Text size="2" className="text-slate-600">
                  Generated on {new Date(plan.uploaded_ts).toLocaleDateString()}
                </Text>
              </Box>
              <Flex gap="4" align="center">
                {([
                  ['Goals', plan.goals.length, 'text-blue-600'],
                  ['Questions', countPrimaryQuestions(plan.questions), 'text-green-600'],
                  ['Tasks', plan.tasks.length, 'text-purple-600'],
                  ['ICD-10', plan.icd10_codes.length, 'text-orange-600'],
                ] as const).map(([label, count, color]) => (
                  <Box key={label} className="text-center">
                    <Text size="1" className="text-slate-500 block">{label}</Text>
                    <Text size="3" weight="bold" className={color}>{count}</Text>
                  </Box>
                ))}
              </Flex>
            </Flex>
          </Card>

          {/* Scroll-spy tab bar */}
          <Tabs.Root value={getActiveTab(planIndex)} onValueChange={(tab) => handleTabClick(planIndex, tab as TabName)}>
            <Tabs.List className="mb-4">
              <Tabs.Trigger value="goals">
                <StarIcon width="14" height="14" />
                Goals ({plan.goals.length})
              </Tabs.Trigger>
              <Tabs.Trigger value="questions">
                <QuestionMarkIcon width="14" height="14" />
                Questions ({countPrimaryQuestions(plan.questions)})
              </Tabs.Trigger>
              <Tabs.Trigger value="tasks">
                <CheckIcon width="14" height="14" />
                Tasks ({plan.tasks.length})
              </Tabs.Trigger>
              <Tabs.Trigger value="icd10">
                <FileTextIcon width="14" height="14" />
                ICD-10 ({plan.icd10_codes.length})
              </Tabs.Trigger>
            </Tabs.List>
          </Tabs.Root>

          {/* Scrollable sections */}
          <ScrollArea
            ref={(el: HTMLDivElement | null) => { scrollAreaRefs.current[planIndex] = el; }}
            style={{ height: '40vh' }}
            onScroll={() => handleScrollSpy(planIndex)}
          >
            <div ref={(el) => { sectionRefs.current[`${planIndex}-goals`] = el; }}>
              {renderEditableGoals(plan.goals, planIndex)}
            </div>
            <div ref={(el) => { sectionRefs.current[`${planIndex}-questions`] = el; }} className="mt-6">
              {renderEditableQuestions(plan.questions, planIndex)}
            </div>
            <div ref={(el) => { sectionRefs.current[`${planIndex}-tasks`] = el; }} className="mt-6">
              {renderEditableTasks(plan.tasks, planIndex)}
            </div>
            <div ref={(el) => { sectionRefs.current[`${planIndex}-icd10`] = el; }} className="mt-6">
              <ConditionICD10Section
                icd10Codes={plan.icd10_codes}
                planIndex={planIndex}
                clinicalDiseaseOptions={getClinicalDiseaseOptionsForPlan(plan, generationSelectedDiseases)}
                onAddCode={handleAddICD10Code}
                onRemoveCode={handleRemoveICD10Code}
              />
            </div>
          </ScrollArea>

          {planIndex < editableCarePlans.length - 1 && <Separator className="mt-8" />}
        </Box>
      ))}

      {/* PHA-1162: Voice of the Patient — patient-level, so it is ONE section rather than a tab
          repeated on every condition card. Sits after the clinical goals, before the General
          questions. Saves immediately (approvePcmIntake); during first-time enrolment the save
          defers surveys to completeOnboarding (PHA-895). */}
      {carePlanGenerated && voiceOfPatientRequired && (
        <Box ref={vopSectionRef} className="mb-8" data-testid="vop-approval-section">
          <Card className={`p-4 mb-4 bg-gradient-to-r ${voiceOfPatientMissing ? 'from-red-50 to-orange-50' : 'from-emerald-50 to-teal-50'}`}>
            <Flex justify="between" align="center" gap="3">
              <Box>
                <Heading size="4" className="text-slate-800 mb-1">Voice of the Patient</Heading>
                <Text size="2" className="text-slate-600">
                  What matters most to the patient, in their own words. Required for PCMH clinics.
                </Text>
              </Box>
              <Flex gap="4" align="center">
                <Box className="text-center">
                  <Text size="1" className="text-slate-500 block">Goals</Text>
                  <Text size="3" weight="bold" className={voiceOfPatientMissing ? 'text-red-600' : 'text-emerald-600'}>
                    {vopGoals.length}
                  </Text>
                </Box>
                <Badge color={voiceOfPatientMissing ? 'red' : 'green'} size="2">
                  {voiceOfPatientMissing ? 'Required' : '✓ Complete'}
                </Badge>
              </Flex>
            </Flex>
          </Card>

          {vopGoals.map((goal: any) => (
            <Card key={goal.pco_ref} className="p-3 mb-2">
              <Flex justify="between" align="start" gap="3">
                <Box>
                  <Text size="2" weight="medium" className="text-slate-800 block">{goal.goal_text}</Text>
                  {goal.tracking_question && (
                    <Text size="1" color="gray" className="block mt-1">Tracking: {goal.tracking_question}</Text>
                  )}
                </Box>
                <Button
                  size="1"
                  variant="soft"
                  disabled={vopWizardOpen}
                  onClick={() => {
                    setVopEditingGoal(
                      buildVopGoalEditTarget(goal, patientForPdf?.gas_definitions, patientForPdf?.pcm_barriers, vopGoals.length)
                    );
                    setVopWizardOpen(true);
                  }}
                >
                  <Pencil1Icon width="12" height="12" />
                  Edit
                </Button>
              </Flex>
            </Card>
          ))}

          {vopWizardOpen ? (
            <Box className="border border-slate-200 rounded-md p-4 mt-2">
              <PcmIntakeReviewPanel
                key={vopEditingGoal ? `edit-${vopEditingGoal.pco_ref}` : 'add'}
                patientId={patientId}
                editingGoal={vopEditingGoal}
                existingGoals={vopGoals}
                // Not enrolled yet: completeOnboarding generates the surveys, VoP included.
                deferSurveys={isOnboarding && isFirstTimeEnrollment}
                onLocked={async () => {
                  try {
                    await onVoiceOfPatientSaved?.();
                  } catch (err) {
                    console.warn('PHA-1162: could not refresh Voice of the Patient goals:', err);
                  }
                }}
                onClose={() => {
                  setVopWizardOpen(false);
                  setVopEditingGoal(null);
                }}
              />
            </Box>
          ) : (
            <Button
              variant="soft"
              className="mt-2"
              onClick={() => {
                setVopEditingGoal(null);
                setVopWizardOpen(true);
              }}
            >
              <PlusIcon width="14" height="14" />
              {vopGoals.length > 0 ? 'Add Another Goal' : 'Create Voice of Patient Goal'}
            </Button>
          )}
        </Box>
      )}

      {/* PHA-406 V2: General section — editable hospitalization question */}
      {carePlanGenerated && generalQuestions.length > 0 && (
        <Box className="mb-8">
          <Card className="p-4 mb-6 bg-gradient-to-r from-gray-50 to-slate-50">
            <Flex justify="between" align="center">
              <Box>
                <Heading size="4" className="text-slate-900">{GENERAL_CARE_PLAN_TITLE}</Heading>
                <Text size="2" color="gray">Applies to all conditions</Text>
              </Box>
              <Flex gap="4" align="center">
                <Flex direction="column" align="center">
                  <Text size="4" weight="bold" className="text-blue-600">{countPrimaryQuestions(generalQuestions)}</Text>
                  <Text size="1" color="gray">Questions</Text>
                </Flex>
              </Flex>
            </Flex>
          </Card>

          <Tabs.Root defaultValue="questions">
            <Tabs.List>
              <Tabs.Trigger value="questions">? Questions ({countPrimaryQuestions(generalQuestions)})</Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="questions" className="mt-4">
              <Box className="space-y-3">
                {generalQuestions.map((question, qIdx) => (
                  <Card key={qIdx} className="p-4">
                    <Flex justify="between" align="start" gap="4">
                      <Box className="flex-1">
                        <Text size="3" weight="medium" className="mb-2 block">
                          {question.prompt}
                        </Text>
                        {(() => {
                          // PHA-815: backend-computed cadence (single source of truth),
                          // honoring any in-session clinician override on question.frequency.
                          const cadence = cronToCadence(resolveQuestionCadence(question));
                          const badgeColor = cadence ? getCadenceBadgeColor(cadence.cadence_type) : 'gray';
                          return <Badge color={badgeColor as any} variant="soft" size="1">{formatCadenceBadge(cadence)}</Badge>;
                        })()}
                      </Box>
                      <Flex gap="1">
                        <IconButton size="1" variant="ghost" onClick={() => handleEditItem('question', question, -1)}>
                          <Pencil1Icon />
                        </IconButton>
                        <IconButton size="1" variant="ghost" color="red" onClick={() => {
                          setGeneralQuestions(prev => prev.filter((_, i) => i !== qIdx));
                        }}>
                          <TrashIcon />
                        </IconButton>
                      </Flex>
                    </Flex>
                  </Card>
                ))}
                <Flex justify="end" className="mt-3">
                  <Button size="2" variant="soft" onClick={() => {
                    setEditDialog({
                      open: true, type: 'question',
                      item: { prompt: '', data_type: 'str', frequency: 'unscheduled', question_index: generalQuestions.length, required: false },
                      isNew: true, planIndex: -1
                    });
                  }}>
                    <PlusIcon /> Add Question
                  </Button>
                </Flex>
              </Box>
            </Tabs.Content>
          </Tabs.Root>
        </Box>
      )}

      {/* PHA-897/PHA-726: a care-plan REFRESH collects an acknowledgment, not consent.
          Programme consent is one-time and drives the billing start date (PHA-953); asking
          for it again on every update overwrote the original enrollment date and asked the
          patient to consent to something they were already enrolled in.
          PHA-1119: "already enrolled" is the condition, not "is a regeneration" — a
          mid-onboarding patient regenerating their draft still gets the consent block below. */}
      {!isFirstTimeEnrollment && (
        <>
          <Separator className="my-6" />
          <Box className="mb-6">
            <Text size="3" weight="bold" className="text-slate-800 block mb-1">
              Care plan reviewed with patient <span className="text-red-500">*</span>
            </Text>
            <Text size="2" className="text-slate-500 block mb-3">
              Required before the updated care plan can be approved. The patient's original
              program consent is unchanged.
            </Text>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={reviewedWithPatient}
                onChange={(e) => setReviewedWithPatient(e.target.checked)}
                className="accent-green-600 mt-1"
              />
              <Text size="2" className="text-slate-700">
                I have reviewed this care plan with the patient
              </Text>
            </label>
          </Box>
        </>
      )}

      {/* Patient Consent — first-time onboarding only. */}
      {isFirstTimeEnrollment && (
      <>
      <Separator className="my-6" />
      <Box className="mb-6">
        <Text size="3" weight="bold" className="text-slate-800 block mb-1">
          Patient Consent <span className="text-red-500">*</span>
        </Text>
        <Text size="2" className="text-slate-500 block mb-3">
          Consent selection is required to complete onboarding.
        </Text>
        <Flex direction="column" gap="2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="consent"
              value="yes"
              checked={consentStatus === 'yes'}
              onChange={() => setConsentStatus('yes')}
              className="accent-green-600"
            />
            <Text size="2" className="text-slate-700">Patient has given consent</Text>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="consent"
              value="no"
              checked={consentStatus === 'no'}
              onChange={() => setConsentStatus('no')}
              className="accent-green-600"
            />
            <Text size="2" className="text-slate-700">Patient has declined consent</Text>
          </label>
        </Flex>
      </Box>
      </>
      )}

      {/* Care Plan Review Date (required, PHA-1285) */}
      <Separator className="my-6" />
      <Box className="mb-6">
        <Text size="3" weight="bold" className="text-slate-800 block mb-1">
          Care Plan Review Date
        </Text>
        <Text size="2" className="text-slate-500 block mb-3">
          {/* PHA-1117: a clinic can be configured for a single month, and "1 months" is the
              exact string Zack flagged. The number itself comes from the clinic setting. */}
          Default is {carePlanReviewMonths} {carePlanReviewMonths === 1 ? 'month' : 'months'} from plan creation, per this clinic's setting. Adjust if needed.
        </Text>
        <TextField.Root style={{ maxWidth: 220 }}>
          <TextField.Input
            type="text"
            inputMode="numeric"
            placeholder="MM/DD/YYYY"
            maxLength={10}
            value={expirationDate}
            onChange={(e) => {
              expirationDateTouchedRef.current = true;
              setExpirationDate(maskDateInput(e.target.value));
            }}
          />
        </TextField.Root>
        {!isFutureMaskedDate(expirationDate) && (
          <Text size="1" color="red" className="block mt-1">
            {expirationDate ? 'Enter a valid future date (MM/DD/YYYY).' : 'A review date is required (MM/DD/YYYY).'}
          </Text>
        )}
      </Box>

      {/* PHA-737: Draft utility action (Print Draft) — not gated on consent.
          PHA-903 removed "Save as Draft" from here in both render contexts; PHA-967 brings it
          back for ONBOARDING ONLY (via onSaveDraftAndExit, rendered next to Approve below) — not
          as a redundant save, but as the missing exit CTA that also routes to the patient chart.
          The PatientDetail draft-review modal still has no save button: that flow ends in
          Approve. Print Draft is always active (blue). */}
      {patientForPdf && (
        <>
          <Separator className="my-6" />
          <Flex gap="3" justify="center" className="mb-4">
            <Button
              size="2"
              variant="soft"
              color="blue"
              onClick={handlePrintDraft}
            >
              <FileTextIcon width="14" height="14" />
              Print Draft
            </Button>
            {/* PHA-626: on the chart the script lives in the header's Actions menu, not on the
                care plan. The onboarding wizard has no chart header, so it keeps this button. */}
            {isOnboarding && (
              <Button
                size="2"
                variant="soft"
                color="blue"
                onClick={handleOpenOnboardingScript}
                data-testid="onboarding-script-button"
              >
                <FileTextIcon width="14" height="14" />
                Onboarding Script
              </Button>
            )}
          </Flex>
        </>
      )}

      {/* Approve */}
      <Separator className="my-3" />
      {hasEmptyICD10 && (
        <Callout.Root color="orange" className="mb-4">
          <Callout.Text>
            Each care plan must have at least one ICD-10 code before approval. Missing: {plansMissingICD10.join(', ')}.
          </Callout.Text>
        </Callout.Root>
      )}
      {carePlanGenerated && voiceOfPatientMissing && (
        <Callout.Root color="orange" className="mb-4">
          <Callout.Text>{VOICE_OF_PATIENT_REQUIRED_MESSAGE}</Callout.Text>
        </Callout.Root>
      )}
      <Flex justify="center" gap="3" wrap="wrap">
        {/* PHA-967: a clinician who does not want to approve yet had no CTA at all and had to
            work out how to escape the wizard. "Save as Draft" is that exit — it saves and lands
            them on the patient's chart, on the Care Plans tab, looking at what they just saved. */}
        {onSaveDraftAndExit && (
          <Button
            size="3"
            variant="soft"
            color="gray"
            onClick={handleSaveDraftAndExit}
            disabled={loading || approving || savingDraft}
            className="min-w-48"
          >
            {savingDraft ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-600"></div>
                Saving…
              </div>
            ) : (
              <>
                <FileTextIcon width="16" height="16" />
                Save as Draft
              </>
            )}
          </Button>
        )}
        {/* PHA-1049: the third exit from a refresh. Without it the only ways out of a
            regeneration were to approve it or abandon the tab, and abandoning left the
            generated draft on the chart competing with the plan the clinician meant to keep. */}
        {onCancelRegeneration && (
          <Button
            size="3"
            variant="soft"
            color="red"
            onClick={() => setCancelRegenerationOpen(true)}
            disabled={loading || approving || savingDraft || cancellingRegeneration}
            className="min-w-48"
          >
            <Cross2Icon width="16" height="16" />
            Cancel Care Plan Regeneration
          </Button>
        )}
        {isFirstTimeEnrollment && consentStatus === 'no' ? (
          <Button
            size="3"
            color="red"
            onClick={() => {
              skipExitFlushRef.current = true; // PHA-1285: declining ends the draft
              onDeclineConsent();
            }}
            disabled={loading || savingDraft}
            className="min-w-48"
          >
            Exit Onboarding
          </Button>
        ) : (
          <Button
            size="3"
            onClick={handleApproveCarePlan}
            // PHA-897: an update is gated on the acknowledgment checkbox, an enrolment on
            // the consent selection. Never both — exactly one of those two blocks renders,
            // and PHA-1119 is what decides which (see `isFirstTimeEnrollment`).
            disabled={
              loading || approving || savingDraft || hasEmptyICD10 ||
              (isFirstTimeEnrollment ? consentStatus === null : !reviewedWithPatient)
            }
            className="min-w-48"
          >
            {approving ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {uploadingToEhr ? 'Uploading to EHR...' : 'Approving...'}
              </div>
            ) : (
              <>
                Approve Care Plan
                <CheckIcon width="16" height="16" />
              </>
            )}
          </Button>
        )}
      </Flex>

      {/* PHA-1049: confirm before discarding a regeneration — it deletes generated work. */}
      <Dialog.Root open={cancelRegenerationOpen} onOpenChange={setCancelRegenerationOpen}>
        <Dialog.Content style={{ maxWidth: 460 }}>
          <Dialog.Title>Cancel care plan regeneration?</Dialog.Title>
          <Dialog.Description size="2" className="mb-4">
            {refreshHasApprovedPlan === false
              ? 'The care plan you just generated will be discarded. This patient has no approved care plan, so they will be left without one until a new plan is generated and approved.'
              : 'The care plan you just generated will be discarded. The patient keeps their current approved care plan — nothing about it changes.'}
          </Dialog.Description>
          <Flex gap="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setCancelRegenerationOpen(false)}
              disabled={cancellingRegeneration}
            >
              Keep working
            </Button>
            <Button
              color="red"
              disabled={cancellingRegeneration}
              onClick={async () => {
                setCancellingRegeneration(true);
                skipExitFlushRef.current = true; // PHA-1285: never re-save a discarded draft
                try {
                  await onCancelRegeneration?.();
                  setCancelRegenerationOpen(false);
                } finally {
                  setCancellingRegeneration(false);
                }
              }}
            >
              {cancellingRegeneration ? 'Discarding…' : 'Discard regeneration'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {/* PHA-1157: confirm before replacing the draft with the standard-guideline plan. */}
      <Dialog.Root open={resetToGuidelinesOpen} onOpenChange={setResetToGuidelinesOpen}>
        <Dialog.Content style={{ maxWidth: 460 }}>
          <Dialog.Title>Reset to standard guidelines?</Dialog.Title>
          <Dialog.Description size="2" className="mb-4">
            This replaces the goals, tasks, questions and plain-English plan in this draft with
            freshly generated standard-guideline content. Edits carried over from the previous
            care plan and edits you made here will be lost. The patient's approved care plan is
            not changed until you approve this draft.
          </Dialog.Description>
          <Flex gap="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setResetToGuidelinesOpen(false)}
              disabled={resettingToGuidelines}
            >
              Keep my edits
            </Button>
            <Button
              color="red"
              disabled={resettingToGuidelines}
              onClick={async () => {
                if (!onRegenerateCarePlan) return;
                setResettingToGuidelines(true);
                try {
                  const diseases = resolveRegenerateSelection(
                    lastGenerationSelectionRef.current,
                    selectedDiseasesList,
                    carePlanData?.care_plan_definitions,
                    matchedDiseases,
                    suggestGeneral
                  );
                  await onRegenerateCarePlan(diseases, { resetToGuidelines: true });
                  setResetToGuidelinesOpen(false);
                } catch (error) {
                  console.error('Reset to standard guidelines failed:', error);
                } finally {
                  setResettingToGuidelines(false);
                }
              }}
            >
              {resettingToGuidelines ? 'Resetting…' : 'Reset to guidelines'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      {renderEditDialog()}
      <OnboardingScriptDialog
        open={onboardingScript !== null}
        onOpenChange={(o) => !o && setOnboardingScript(null)}
        script={onboardingScript}
        notice={onboardingScriptNotice}
        patientId={patientForPdf?.patient_id || patientId}
      />
    </Card>
  );
};

export default CarePlanApproval;
